import * as fs from 'fs';
import * as path from 'path';
import { isMatrixDropdownVariant, type MatrixVariantRow } from './survey-inventory-extract';

const SOURCE_DIRS = ['src', 'components', 'app', 'lib'] as const;

const INTRO_PAGE_CANDIDATES = [
  'src/components/IntroPage.tsx',
  'components/IntroPage.tsx',
  'app/components/IntroPage.tsx',
] as const;

const SURVEY_PAGE_CANDIDATES = [
  'src/components/SurveyPage.tsx',
  'components/SurveyPage.tsx',
  'app/components/SurveyPage.tsx',
] as const;

const END_PAGE_CANDIDATES = [
  'src/components/EndPage.tsx',
  'components/EndPage.tsx',
  'app/components/EndPage.tsx',
] as const;

const QUESTION_CANDIDATES = [
  'src/components/Question.tsx',
  'components/Question.tsx',
  'app/components/Question.tsx',
] as const;

const QUESTION_MEDIA_SHELL_CANDIDATES = [
  'src/components/QuestionShellLayout.tsx',
  'src/components/QuestionMediaBlock.tsx',
  'src/components/MediaAttachmentsGrid.tsx',
  'components/QuestionShellLayout.tsx',
  'components/QuestionMediaBlock.tsx',
  'components/MediaAttachmentsGrid.tsx',
] as const;

function readQuestionMediaShellSource(projectRoot: string, questionRel: string): string {
  const chunks = [fs.readFileSync(path.join(projectRoot, questionRel), 'utf8')];
  for (const rel of QUESTION_MEDIA_SHELL_CANDIDATES) {
    const filePath = path.join(projectRoot, rel);
    if (fs.existsSync(filePath)) {
      chunks.push(fs.readFileSync(filePath, 'utf8'));
    }
  }
  return chunks.join('\n');
}

const MATRIX_SCALE_FILES = [
  'CsatMatrixScale.tsx',
  'SliderMatrixScale.tsx',
  'LikertMatrixScale.tsx',
] as const;

const MATRIX_RELATED_FILES = [
  ...MATRIX_SCALE_FILES,
  'MatrixDropdown.tsx',
] as const;

const AGENT_AUTHORED_STYLE_FILES = [
  'src/components/SurveyPage.tsx',
  'components/SurveyPage.tsx',
  'app/components/SurveyPage.tsx',
  'src/globals.css',
  'src/index.css',
  'src/app/globals.css',
  'app/globals.css',
  'src/app/layout.tsx',
  'app/layout.tsx',
] as const;

const HARDCODED_HEX = /#[0-9A-Fa-f]{3,8}\b/;
const HARDCODED_RGB = /\brgba?\s*\(/;

function collectSourceFiles(projectRoot: string): string[] {
  const files: string[] = [];
  for (const dir of SOURCE_DIRS) {
    const abs = path.join(projectRoot, dir);
    if (!fs.existsSync(abs)) continue;
    walk(abs, files);
  }
  return files;
}

function walk(dir: string, out: string[]): void {
  for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
    const abs = path.join(dir, entry.name);
    if (entry.isDirectory()) {
      if (entry.name === 'node_modules' || entry.name === '.next' || entry.name === 'dist') {
        continue;
      }
      walk(abs, out);
    } else if (/\.(tsx?|jsx?|mjs|cjs)$/.test(entry.name)) {
      out.push(abs);
    }
  }
}

function findFirstExisting(projectRoot: string, candidates: readonly string[]): string | null {
  for (const rel of candidates) {
    const abs = path.join(projectRoot, rel);
    if (fs.existsSync(abs)) return rel;
  }
  return null;
}

function isAllowedStyleLine(line: string): boolean {
  const trimmed = line.trim();
  if (trimmed.startsWith('//') || trimmed.startsWith('*') || trimmed.startsWith('/*')) return true;
  if (trimmed.includes('var(--cfm-')) return true;
  if (/url\s*\(#/.test(trimmed)) return true;
  return false;
}

export function scanHardcodedStyleIssues(projectRoot: string): string[] {
  const issues: string[] = [];

  for (const rel of AGENT_AUTHORED_STYLE_FILES) {
    const abs = path.join(projectRoot, rel);
    if (!fs.existsSync(abs)) continue;

    const content = fs.readFileSync(abs, 'utf8');
    const lines = content.split('\n');

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (isAllowedStyleLine(line)) continue;
      if (HARDCODED_HEX.test(line) || HARDCODED_RGB.test(line)) {
        issues.push(`${rel}:${i + 1}: hardcoded color — use survey-ui-config.json + var(--cfm-*)`);
      }
    }
  }

  return issues;
}

export function scanIntroStartIssues(projectRoot: string): string[] {
  const issues: string[] = [];
  const introRel = findFirstExisting(projectRoot, INTRO_PAGE_CANDIDATES);

  if (!introRel) {
    issues.push(
      'IntroPage.tsx missing — author an intro page with a Start button that dispatches onAction START',
    );
    return issues;
  }

  const introContent = fs.readFileSync(path.join(projectRoot, introRel), 'utf8');

  if (!/data-cfm-btn-start/.test(introContent)) {
    issues.push(`${introRel}: Start button missing data-cfm-btn-start`);
  }
  if (!/onClick=\{onStart\}|onClick=\{\s*onStart\s*\}/.test(introContent)) {
    issues.push(`${introRel}: Start button must use onClick={onStart}`);
  }
  if (/\{showStart &&|showStart &&/.test(introContent)) {
    issues.push(`${introRel}: conditional Start (showStart) — always render Start button`);
  }

  const surveyRel = findFirstExisting(projectRoot, SURVEY_PAGE_CANDIDATES);
  if (!surveyRel) return issues;

  const surveyContent = fs.readFileSync(path.join(projectRoot, surveyRel), 'utf8');
  const hasIntroPhase = /phase\.kind\s*===\s*['"]intro['"]|kind\s*===\s*['"]intro['"]/.test(
    surveyContent,
  );

  if (hasIntroPhase && !/<IntroPage/.test(surveyContent)) {
    issues.push(
      `${surveyRel}: intro phase must render <IntroPage /> — do not inline header/description only`,
    );
  }
  if (/<IntroPage/.test(surveyContent) && !/type:\s*['"]START['"]/.test(surveyContent)) {
    issues.push(`${surveyRel}: IntroPage must wire onAction({ type: 'START' })`);
  }

  return issues;
}

function endPageFiltersEmailCtAs(content: string): boolean {
  if (/endPageCtas\s*\(/.test(content)) return true;
  if (/sendEmailResponse,\s*resumptionNotification,\s*reopenNotification/.test(content)) {
    return true;
  }
  return false;
}

export function scanEndPageCtaIssues(projectRoot: string): string[] {
  const endRel = findFirstExisting(projectRoot, END_PAGE_CANDIDATES);
  if (!endRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, endRel), 'utf8');
  if (!/<SpecialPageCtaSection/.test(content)) return [];

  if (endPageFiltersEmailCtAs(content)) return [];

  if (/ctas=\{[^}]*page\.ctas[^}]*\}/.test(content)) {
    return [
      `${endRel}: EndPage must filter email CTAs via endPageCtas(page.ctas) before SpecialPageCtaSection — see 15-end-page.md`,
    ];
  }

  return [];
}

export interface MatrixVariantPlanRow {
  questionId: string;
  type: string;
  displayStyle: string;
  gridLayout: string;
  statementLayout: string;
  transposeTable: string;
  selectionMode: string;
  hasNotApplicableOption: string;
  branchOn?: string;
  scaleAnchorLabelCount?: string;
  matrixFormat?: string;
  nonNaM?: string;
}

function parseTruthyFlag(value: string | undefined): boolean {
  if (!value) return false;
  const v = value.trim().toLowerCase();
  return v === 'true' || v === 'yes';
}

export function readMatrixVariantsFromPlan(projectRoot: string): MatrixVariantPlanRow[] {
  const planPath = path.join(projectRoot, 'implementation_plan.md');
  if (!fs.existsSync(planPath)) return [];

  const content = fs.readFileSync(planPath, 'utf8');
  const section = content.split('## 3c. Matrix variant inventory')[1];
  if (!section) return [];

  const tablePart = section.split('##')[0] ?? '';
  const lines = tablePart.split('\n').filter((l) => l.trim().startsWith('|'));
  const rows: MatrixVariantPlanRow[] = [];

  for (const line of lines) {
    if (line.includes('questionId')) continue;
    // Skip markdown separator rows (only pipes, dashes, colons, whitespace) — including
    // multi-column separators like `|------|------|` that a single-dash regex misses.
    if (/^[\s|:-]+$/.test(line)) continue;
    if (line.includes('(no matrix questions in fetch)')) continue;

    const cells = line
      .split('|')
      .map((c) => c.trim())
      .filter(Boolean);
    if (cells.length < 6) continue;

    if (cells.length >= 12) {
      rows.push({
        questionId: cells[0],
        type: cells[1],
        displayStyle: cells[2],
        gridLayout: cells[3],
        statementLayout: cells[4],
        transposeTable: cells[5],
        selectionMode: cells[6],
        hasNotApplicableOption: cells[7],
        branchOn: cells[8],
        scaleAnchorLabelCount: cells[9],
        matrixFormat: cells[10],
        nonNaM: cells[11],
      });
      continue;
    }

    if (cells.length >= 10) {
      rows.push({
        questionId: cells[0],
        type: cells[1],
        displayStyle: cells[2],
        gridLayout: cells[3],
        statementLayout: cells[4],
        transposeTable: cells[5],
        selectionMode: cells[6],
        hasNotApplicableOption: cells[7],
        branchOn: cells[8],
        scaleAnchorLabelCount: cells[9],
      });
      continue;
    }

    if (cells.length >= 9) {
      rows.push({
        questionId: cells[0],
        type: cells[1],
        displayStyle: cells[2],
        gridLayout: cells[3],
        statementLayout: cells[4],
        transposeTable: cells[5],
        selectionMode: cells[6],
        hasNotApplicableOption: cells[7],
        branchOn: cells[8],
      });
      continue;
    }

    if (cells.length >= 8) {
      rows.push({
        questionId: cells[0],
        type: cells[1],
        displayStyle: cells[2],
        gridLayout: cells[3],
        statementLayout: cells[4],
        transposeTable: cells[5],
        selectionMode: cells[6],
        hasNotApplicableOption: cells[7],
      });
      continue;
    }

    // Legacy §3c (6 columns, no gridLayout / hasNA)
    rows.push({
      questionId: cells[0],
      type: cells[1],
      displayStyle: cells[2],
      gridLayout: '—',
      statementLayout: cells[3],
      transposeTable: cells[4],
      selectionMode: cells[5],
      hasNotApplicableOption: cells[6] ?? '—',
    });
  }

  return rows;
}

export interface RankOrderVariantPlanRow {
  questionId: string;
  interactionMode: string;
  optionDisplay: string;
  branchOn: string;
}

export function readRankOrderVariantsFromPlan(projectRoot: string): RankOrderVariantPlanRow[] {
  const planPath = path.join(projectRoot, 'implementation_plan.md');
  if (!fs.existsSync(planPath)) return [];

  const content = fs.readFileSync(planPath, 'utf8');
  const section = content.split('## 3c-r. Rank order variant inventory')[1];
  if (!section) return [];

  const tablePart = section.split('##')[0] ?? '';
  const lines = tablePart.split('\n').filter((l) => l.trim().startsWith('|'));
  const rows: RankOrderVariantPlanRow[] = [];

  for (const line of lines) {
    if (line.includes('questionId')) continue;
    if (/^[\s|:-]+$/.test(line)) continue;
    if (line.includes('(no rank order questions in fetch)')) continue;

    const cells = line
      .split('|')
      .map((c) => c.trim())
      .filter(Boolean);
    if (cells.length < 3) continue;

    rows.push({
      questionId: cells[0],
      interactionMode: cells[1],
      optionDisplay: cells[2],
      branchOn: cells[3] ?? '—',
    });
  }

  return rows;
}

export interface MediaVariantPlanRow {
  questionId: string;
  type: string;
  hasContainerMedia: string;
  alignment: string;
  size: string;
  attachmentCount: string;
  enablePreview: string;
  enableDownload: string;
}

export function readMediaVariantsFromPlan(projectRoot: string): MediaVariantPlanRow[] {
  const planPath = path.join(projectRoot, 'implementation_plan.md');
  if (!fs.existsSync(planPath)) return [];

  const content = fs.readFileSync(planPath, 'utf8');
  const section = content.split('## 3c-m. Question media inventory')[1];
  if (!section) return [];

  const tablePart = section.split('##')[0] ?? '';
  const lines = tablePart.split('\n').filter(l => l.trim().startsWith('|'));
  const rows: MediaVariantPlanRow[] = [];

  for (const line of lines) {
    if (line.includes('questionId')) continue;
    if (/^[\s|:-]+$/.test(line)) continue;
    if (line.includes('(no question media in fetch)')) continue;

    const cells = line
      .split('|')
      .map(c => c.trim())
      .filter(Boolean);
    if (cells.length < 8) continue;

    rows.push({
      questionId: cells[0],
      type: cells[1],
      hasContainerMedia: cells[2],
      alignment: cells[3],
      size: cells[4],
      attachmentCount: cells[5],
      enablePreview: cells[6],
      enableDownload: cells[7],
    });
  }

  return rows;
}

function readScaleValuesContent(projectRoot: string): string {
  for (const file of collectSourceFiles(projectRoot)) {
    if (path.basename(file) === 'scaleValues.ts') {
      return fs.readFileSync(file, 'utf8');
    }
  }
  return '';
}

function matrixScaleFileForType(type: string): string | undefined {
  if (type === 'CFM_MATRIX') return 'LikertMatrixScale.tsx';
  if (type === 'CSAT_MATRIX' || type === 'RATING_MATRIX') return 'CsatMatrixScale.tsx';
  if (type === 'SLIDER_MATRIX') return 'SliderMatrixScale.tsx';
  return undefined;
}

function extractFunctionBody(content: string, name: string): string {
  const re = new RegExp(`function\\s+${name}\\b[\\s\\S]*?(?=\\nfunction\\s+\\w|$)`);
  return content.match(re)?.[0] ?? '';
}

function stripHoverTooltipBlocks(source: string): string {
  return source.replace(/\{isHovered\s*&&[\s\S]*?\}\s*\)\s*\}/g, '');
}

function numberedCellUsesColLabel(content: string): boolean {
  for (const fn of ['CsatMatrixGrid', 'CsatMatrixCarousel']) {
    const body = extractFunctionBody(content, fn);
    if (!body || !/\bisNumbered\b/.test(body)) continue;

    const stripped = stripHoverTooltipBlocks(body);
    if (!/displayIdx\s*\+\s*1/.test(body) && /col\.label/.test(stripped)) {
      return true;
    }

    if (
      /isNumbered[\s\S]{0,3500}col\.label/.test(stripped) &&
      (/<\/button>[\s\S]{0,600}col\.label/.test(stripped) ||
        /dangerouslySetInnerHTML[\s\S]{0,80}col\.label/.test(stripped))
    ) {
      return true;
    }
  }

  return false;
}

function anchorUsesJustifyBetweenWithoutAbsolute(content: string): boolean {
  if (!/scaleAnchorLabels/.test(content)) return false;

  const anchorBlocks = content.match(/scaleAnchorLabels[\s\S]{0,1800}/g);
  if (!anchorBlocks?.length) return false;

  return anchorBlocks.some((block) => {
    const hasJustify =
      /justify-between/.test(block) ||
      /justifyContent:\s*['"]space-between['"]/.test(block);
    const hasAbsolute = /position:\s*['"]absolute['"]/.test(block);
    return hasJustify && !hasAbsolute;
  });
}

function likertMissingNonNaScaleColumns(content: string): boolean {
  if (!/scaleColumns|gridCols/.test(content)) return false;
  return !/nonNaScaleColumns/.test(content);
}

function likertNaInGridLoop(content: string): boolean {
  if (/nonNaScaleColumns/.test(content)) return false;
  return /gridCols\.map|scaleColumns\.map/.test(content);
}

function csatCarouselDuplicateLabels(content: string): boolean {
  const carousel = extractFunctionBody(content, 'CsatMatrixCarousel');
  if (!carousel) return false;
  if (!/\bisNumbered\b|\bisEmoji\b|\bisStar\b/.test(carousel)) return false;
  const stripped = stripHoverTooltipBlocks(carousel);
  return (
    /<\/button>[\s\S]{0,600}col\.label/.test(stripped) ||
    /dangerouslySetInnerHTML[\s\S]{0,80}col\.label/.test(stripped)
  );
}

function csatNaHeaderGapWhenAnchors(content: string): boolean {
  if (!/scaleAnchorLabels/.test(content) || !/hasNotApplicable/.test(content)) return false;

  const showLabelsBlock = content.match(/showLabels[\s\S]{0,2500}/);
  if (!showLabelsBlock) return false;

  const block = showLabelsBlock[0];
  const reservesNaSlot = /hasNotApplicable[\s\S]{0,120}flexShrink:\s*0/.test(block);
  const hasNaHeaderText = /['"]N\/A['"]|Not Applicable|>\s*N\/A\s*</.test(block);
  return reservesNaSlot && !hasNaHeaderText;
}

function likertBipolarStructureWeak(content: string): boolean {
  if (!/oppositeStatementText/.test(content)) return true;
  const hasCanonicalSpacers = /0\s*0\s*25%|flex:\s*['"]0\s+0\s+25%['"]/.test(content);
  const hasFlexMiddle = /flex-1|flex:\s*1\b|flex:\s*['"]1['"]/.test(content);
  if (hasCanonicalSpacers && hasFlexMiddle) {
    return bipolarGridOnlyWithoutMiddleFlex(content);
  }
  return true;
}

function bipolarUsesW14Only(content: string): boolean {
  if (!/w-1\/4/.test(content)) return false;
  const bipolarBlocks =
    content.match(
      /[\s\S]{0,160}(?:oppositeStatementText|isBipolar\s*\?)[\s\S]{0,900}/g,
    ) ?? [];
  return bipolarBlocks.some(
    (block) =>
      /w-1\/4/.test(block) &&
      !/0\s*0\s*25%|flex:\s*['"]0\s+0\s+25%['"]/.test(block),
  );
}

function bipolarAnchorHeaderUsesLikertGrid(content: string): boolean {
  const headerBody = extractFunctionBody(content, 'renderHeader') || content;
  const anchorBlocks = headerBody.match(/(?:hasLabels|scaleAnchorLabels)[\s\S]{0,1400}/g) ?? [];
  for (const block of anchorBlocks) {
    if (/gridTemplateColumns[\s\S]{0,160}?(?:gridCols|likertGridColumns)/.test(block)) {
      const beforeGrid = block.split(/gridTemplateColumns/)[0] ?? '';
      if (!/0\s+0\s+25%|flex:\s*['"]0\s+0\s+25%['"]/.test(beforeGrid)) {
        return true;
      }
    }
  }
  if (
    /(?:isBipolar|statementLayout\s*===\s*['"]bipolar['"])[\s\S]{0,2500}scaleAnchorLabels[\s\S]{0,1000}gridTemplateColumns[\s\S]{0,100}(?:gridCols|likertGridColumns)/.test(
      content,
    )
  ) {
    const match =
      content.match(
        /(?:isBipolar|statementLayout\s*===\s*['"]bipolar['"])[\s\S]{0,2500}scaleAnchorLabels[\s\S]{0,1000}/,
      )?.[0] ?? '';
    if (!/0\s+0\s+25%|flex:\s*['"]0\s+0\s+25%['"]/.test(match)) {
      return true;
    }
  }
  return false;
}

function bipolarAnchorHeaderMissingFlexSpacers(content: string): boolean {
  if (!/scaleAnchorLabels/.test(content)) return false;
  if (!/(?:isBipolar|statementLayout\s*===\s*['"]bipolar['"])/.test(content)) return false;
  return !/0\s+0\s+25%|flex:\s*['"]0\s+0\s+25%['"]/.test(content);
}

function bipolarRowHasZoneGapWithoutHeaderMatch(content: string): boolean {
  if (!/(?:isBipolar|statementLayout\s*===\s*['"]bipolar['"])/.test(content)) return false;
  const rowBlocks = content.match(/(?:isBipolar|oppositeStatementText)[\s\S]{0,800}/g) ?? [];
  const rowHasGap = rowBlocks.some((b) => /\bgap-\d|\bgap:\s*\d|gap:\s*['"]/.test(b));
  if (!rowHasGap) return false;
  const headerBody = extractFunctionBody(content, 'renderHeader') || '';
  return !/\bgap-\d|\bgap:\s*\d|gap:\s*['"]/.test(headerBody);
}

/** Bipolar using grid `0 0 25%` without a flex-1 middle scale zone. */
function bipolarGridOnlyWithoutMiddleFlex(content: string): boolean {
  if (!/0\s*0\s*25%/.test(content)) return false;
  return !/flex-1|flex:\s*1\b|flex:\s*['"]1['"]/.test(content);
}

function csatNaInGridLoop(content: string): boolean {
  if (/nonNaScaleColumns/.test(content)) return false;
  return /scaleColumns\.map/.test(content);
}

function ratingGraphicsUsesNumericIndex(content: string): boolean {
  if (!/isGraphics|displayStyle\s*===\s*['"]graphics['"]/.test(content)) return false;
  if (!/CustomSliderTrack/.test(content)) return false;
  return (
    /typeof\s+\w+\s*===\s*['"]number['"]/.test(content) ||
    /value=\{\s*\w+\s*\}/.test(content) && /rowAnswers\[|answers\[/.test(content) && !/findIndex/.test(content)
  );
}

function graphicsLabelsInsideStatementLoop(content: string): boolean {
  if (!/isGraphics|displayStyle\s*===\s*['"]graphics['"]/.test(content)) return false;
  if (/statementRows\.map[\s\S]{0,2500}renderGraphicsHeader/.test(content)) return true;
  if (/GraphicsColumnLabelsRow/.test(content)) {
    return /statementRows\.map[\s\S]{0,2500}GraphicsColumnLabelsRow/.test(content);
  }
  if (
    /statementRows\.map[\s\S]{0,3500}CustomSliderTrack/.test(content) &&
    /statementRows\.map[\s\S]{0,3500}scaleColumns\.map[\s\S]{0,1200}(col\.label|dangerouslySetInnerHTML)/.test(
      content,
    )
  ) {
    return true;
  }
  return /statementRows\.map[\s\S]{0,2000}scaleColumns\.map[\s\S]{0,1200}(col\.label|dangerouslySetInnerHTML)/.test(
    content,
  );
}

function extractCsatMatrixGridNonGraphicsCellBlock(content: string): string {
  const body = extractFunctionBody(content, 'CsatMatrixGrid');
  if (!body) return '';
  const graphicsSplit = body.split(/\)\s*:\s*\(/);
  if (graphicsSplit.length >= 2) {
    return graphicsSplit[graphicsSplit.length - 1] ?? body;
  }
  return body;
}

function csatMatrixCellLoopHasCentering(cellBlock: string): boolean {
  if (!/scaleColumns\.map|gridCols\.map/.test(cellBlock)) return true;
  if (/justify-items-center|justifyItems:\s*['"]center['"]/.test(cellBlock)) return true;
  if (/justify-center|justifyContent:\s*['"]center['"]/.test(cellBlock)) return true;
  return false;
}

function csatMatrixCellLoopUsesScaleGrid(cellBlock: string): boolean {
  return (
    /gridTemplateColumns/.test(cellBlock) &&
    (/repeat\s*\(\s*\$\{?\s*(?:scaleColumns\.length|gridCols\.length|m)\s*\}?\s*,\s*1fr\s*\)/.test(cellBlock) ||
      /repeat\s*\([^)]*1fr/.test(cellBlock))
  );
}

function extractCsatMatrixGridNonGraphicsBlock(gridBody: string): string {
  const parts = gridBody.split(/\)\s*:\s*\(/);
  if (parts.length >= 2) {
    return parts[parts.length - 1] ?? gridBody;
  }
  if (/isGraphics/.test(gridBody)) return '';
  return gridBody;
}

function csatMatrixGridMixedNaMode(gridBody: string): boolean {
  if (!gridBody || !/hasNotApplicable/.test(gridBody)) return false;
  const nonGraphics = extractCsatMatrixGridNonGraphicsBlock(gridBody);
  if (!nonGraphics) return false;
  const hasTrailing56 = /width:\s*['"]56px['"]/.test(nonGraphics);
  const hasBelowRow =
    /Not Applicable/.test(nonGraphics) &&
    /hasNotApplicable[\s\S]{0,400}(checkbox|type:\s*['"]checkbox['"])/.test(nonGraphics);
  return hasTrailing56 && hasBelowRow;
}

function csatDenseAnchorLacksGridBranch(content: string): boolean {
  if (!/scaleAnchorLabels/.test(content)) return false;
  const gridBody = extractFunctionBody(content, 'CsatMatrixGrid') || content;
  const anchorRegion = gridBody.match(/showLabels[\s\S]{0,3500}/)?.[0] ?? gridBody;
  const hasGridBranch =
    /n\s*===\s*m[\s\S]{0,1200}gridTemplateColumns/.test(anchorRegion) ||
    /scaleAnchorLabels[\s\S]{0,800}gridTemplateColumns/.test(anchorRegion);
  if (hasGridBranch) return false;
  return /position:\s*['"]absolute['"]/.test(anchorRegion) && /scaleAnchorLabels/.test(anchorRegion);
}

/** §3c-gated: CSAT/RATING star/emoji/numbered grid cells must center controls in columns. */
export function scanCsatMatrixCellCenteringIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const gated = variants.some(
    (v) =>
      (v.type === 'CSAT_MATRIX' || v.type === 'RATING_MATRIX') &&
      /star|emoji|numbered/i.test(v.displayStyle) &&
      !/graphics|dropdown|selectbox/i.test(v.displayStyle) &&
      !/carousel/i.test(v.gridLayout),
  );
  if (!gated) return [];

  const csatRel = findFirstExisting(projectRoot, [
    'src/components/CsatMatrixScale.tsx',
    'components/CsatMatrixScale.tsx',
  ]);
  if (!csatRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, csatRel), 'utf8');
  const cellBlock = extractCsatMatrixGridNonGraphicsCellBlock(content);
  if (!csatMatrixCellLoopUsesScaleGrid(cellBlock)) return [];

  if (csatMatrixCellLoopHasCentering(cellBlock)) return [];

  return [
    `${csatRel}: CSAT matrix cells must be centered in grid columns (justify-items-center or per-cell justify-center) — see layout-cookbook/shared.md §Cell centering (Q7/Q17/Q18)`,
  ];
}

/** §3c-gated: detect mixed or missing N/A wiring in CsatMatrixGrid. */
export function scanCsatMatrixNaPlacementIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const naVariants = variants.filter(
    (v) =>
      parseTruthyFlag(v.hasNotApplicableOption) &&
      (v.type === 'CSAT_MATRIX' || v.type === 'RATING_MATRIX') &&
      !/graphics/i.test(v.displayStyle),
  );
  if (!naVariants.length) return [];

  const csatRel = findFirstExisting(projectRoot, [
    'src/components/CsatMatrixScale.tsx',
    'components/CsatMatrixScale.tsx',
  ]);
  if (!csatRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, csatRel), 'utf8');
  const gridBody = extractFunctionBody(content, 'CsatMatrixGrid');
  const issues: string[] = [];

  if (gridBody && csatMatrixGridMixedNaMode(gridBody)) {
    issues.push(
      `${csatRel}: CSAT matrix N/A uses both trailing 56px and below-row Not Applicable — pick one mode — see 04-csat.md §N/A placement`,
    );
  }

  if (csatNaHeaderGapWhenAnchors(content)) {
    const hasBelowRow = /Not Applicable/.test(content);
    if (!hasBelowRow) {
      issues.push(
        `${csatRel}: CSAT anchor row reserves N/A spacer without N/A header text — use below-row NA or add header label — see layout-cookbook/csat_matrix.md`,
      );
    }
  }

  const hasTrailing56 = gridBody
    ? /width:\s*['"]56px['"]/.test(extractCsatMatrixGridNonGraphicsBlock(gridBody))
    : false;
  const hasBelowRowNa = /Not Applicable/.test(content);
  const hasNaControl =
    hasBelowRowNa ||
    hasTrailing56 ||
    /hasNotApplicable[\s\S]{0,300}(checkbox|type:\s*['"]checkbox['"])/.test(content);

  if (!hasNaControl && /hasNotApplicableOption/.test(content)) {
    issues.push(
      `${csatRel}: §3c hasNA — wire N/A checkbox (below-row or trailing 56px) — see layout-cookbook/csat_matrix.md`,
    );
  }

  const visualChoiceNa = naVariants.filter((v) => /star|emoji|numbered/i.test(v.displayStyle));
  if (visualChoiceNa.length && hasTrailing56 && !hasBelowRowNa && gridBody) {
    issues.push(
      `${MATRIX_NEAR_MISS_PREFIX} ${csatRel}: visual-choice grid with hasNA — prefer below-row Not Applicable (no 56px scale slot) — see 04-csat.md §N/A placement (Q7/Q18)`,
    );
  }

  return issues;
}

/** NEAR-MISS when dense anchors use flex+absolute only (no n===m grid branch). */
export function scanCsatMatrixDenseAnchorGridIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const gated = variants.some(
    (v) =>
      (v.type === 'CSAT_MATRIX' || v.type === 'RATING_MATRIX') &&
      /star|emoji|numbered/i.test(v.displayStyle) &&
      (parseInt(v.scaleAnchorLabelCount ?? '0', 10) > 0 || /anchor/i.test(v.branchOn ?? '')),
  );
  if (!gated) return [];

  const csatRel = findFirstExisting(projectRoot, [
    'src/components/CsatMatrixScale.tsx',
    'components/CsatMatrixScale.tsx',
  ]);
  if (!csatRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, csatRel), 'utf8');
  if (!csatDenseAnchorLacksGridBranch(content)) return [];

  return [
    `${MATRIX_NEAR_MISS_PREFIX} ${csatRel}: when scaleAnchorLabels count equals m, use grid anchor row (n === m branch) — see 04-csat.md §Anchor row structure`,
  ];
}

function extractLikertMatrixGridCellBlock(content: string): string {
  const body = extractFunctionBody(content, 'LikertMatrixGridLayout');
  if (!body) return '';
  if (/isBipolarSlider/.test(body)) {
    const parts = body.split(/\)\s*:\s*\(/);
    if (parts.length >= 2) return parts[parts.length - 1] ?? body;
  }
  return body;
}

/** §3c-gated: CFM standard likert + transpose grid cells must center radios/checkboxes. */
export function scanLikertMatrixCellCenteringIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const gated = variants.some((v) => {
    if (v.type !== 'CFM_MATRIX') return false;
    if (/carousel|dropdown/i.test(v.gridLayout)) return false;
    if (/bipolar/i.test(v.statementLayout)) return false;
    return true;
  });
  if (!gated) return [];

  const likertRel = findFirstExisting(projectRoot, [
    'src/components/LikertMatrixScale.tsx',
    'components/LikertMatrixScale.tsx',
  ]);
  if (!likertRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, likertRel), 'utf8');
  const cellBlock = extractLikertMatrixGridCellBlock(content);
  if (!csatMatrixCellLoopUsesScaleGrid(cellBlock)) return [];
  if (csatMatrixCellLoopHasCentering(cellBlock)) return [];

  return [
    `${likertRel}: CFM likert/transpose cells must be centered in grid columns (justify-items-center or per-cell justify-center) — see layout-cookbook/cfm_matrix.md (Q16/Q17)`,
  ];
}

function matrixScaleFlexOnlyWithoutGrid(content: string): boolean {
  const hasGrid =
    /gridTemplateColumns/.test(content) ||
    /repeat\s*\(\s*\$\{?\s*m\s*\}?/.test(content) ||
    /repeat\s*\([^)]*,\s*1fr\s*\)/.test(content);
  if (hasGrid) return false;
  const hasFlexCells =
    /(?:className=['"][^'"]*gap-|gap:\s*\d+|justify-between|justifyContent:\s*['"]space-between['"])/.test(
      content,
    );
  const hasCellLoop = /scaleColumns\.map|gridCols\.map|nonNaScaleColumns\(/.test(content);
  return hasFlexCells && hasCellLoop;
}

function pushAutoFitIssue(
  issues: string[],
  name: string,
  variantId: string | undefined,
  strict: boolean,
): void {
  const msg = variantId
    ? `Matrix carousel parity (${variantId}): auto-fit minmax in index map — use shared gridTemplateColumns — see layout-cookbook/shared.md`
    : `Matrix carousel parity: auto-fit minmax in ${name} — header/cell index map may drift — see layout-cookbook/shared.md`;
  if (strict) {
    issues.push(msg);
  } else {
    issues.push(`${MATRIX_NEAR_MISS_PREFIX} ${msg}`);
  }
}

function likertCarouselLacksBipolar(content: string): boolean {
  const carousel = extractFunctionBody(content, 'LikertMatrixCarouselLayout');
  if (!carousel) return false;
  return !/oppositeStatementText|isBipolar/.test(carousel);
}

function matrixFileHasAutoFitMinmax(content: string): boolean {
  return /auto-fit\s+minmax|repeat\s*\(\s*auto-fit/i.test(content);
}

function carouselMissingNavSignals(content: string): boolean {
  if (!/Carousel|gridLayout\s*===\s*['"]carousel['"]/.test(content)) return false;
  return !/activeCarouselIndex|setActiveCarousel|Prev|Next|\d+\s*\/\s*\d+|carouselIndex/i.test(
    content,
  );
}

export const MATRIX_NEAR_MISS_PREFIX = 'NEAR-MISS:';

export function isMatrixLayoutNearMiss(issue: string): boolean {
  return issue.startsWith(MATRIX_NEAR_MISS_PREFIX);
}

export function partitionMatrixLayoutIssues(issues: string[]): {
  blocking: string[];
  nearMisses: string[];
} {
  const blocking: string[] = [];
  const nearMisses: string[] = [];
  for (const issue of issues) {
    if (isMatrixLayoutNearMiss(issue)) nearMisses.push(issue);
    else blocking.push(issue);
  }
  return { blocking, nearMisses };
}

function readMatrixFileContents(projectRoot: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const file of collectSourceFiles(projectRoot)) {
    const basename = path.basename(file);
    if (!(MATRIX_RELATED_FILES as readonly string[]).includes(basename)) continue;
    out.set(basename, fs.readFileSync(file, 'utf8'));
  }
  return out;
}

const SURVEY_UI_LIB_DIRS = ['src/lib/surveyUi', 'lib/surveyUi'] as const;

function readSurveyUiLibContents(projectRoot: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const relDir of SURVEY_UI_LIB_DIRS) {
    const absDir = path.join(projectRoot, relDir);
    if (!fs.existsSync(absDir)) continue;
    const files: string[] = [];
    walk(absDir, files);
    for (const file of files) {
      if (!/\.(tsx?|jsx?)$/.test(file)) continue;
      out.set(path.relative(absDir, file), fs.readFileSync(file, 'utf8'));
    }
  }
  return out;
}

function readMatrixLayoutFiles(projectRoot: string): {
  components: Map<string, string>;
  lib: Map<string, string>;
  all: Map<string, string>;
} {
  const components = readMatrixFileContents(projectRoot);
  const lib = readSurveyUiLibContents(projectRoot);
  const all = new Map<string, string>([...components, ...lib]);
  return { components, lib, all };
}

function readMatrixLayoutBlob(projectRoot: string): string {
  const { all } = readMatrixLayoutFiles(projectRoot);
  return [...all.entries()].map(([name, content]) => `// ${name}\n${content}`).join('\n');
}

function hasValidMatrixGridSkeleton(content: string): boolean {
  if (!/gridTemplateColumns/.test(content)) return false;
  return (
    /repeat\s*\(\s*\$\{?\s*m\s*\}?/.test(content) ||
    /repeat\s*\([^)]*,\s*1fr\s*\)/.test(content)
  );
}

function matrixScaleFlexOnlyWithoutGridOr(componentContent: string, libBlob: string): boolean {
  if (hasValidMatrixGridSkeleton(componentContent) || hasValidMatrixGridSkeleton(libBlob)) {
    return false;
  }
  return (
    matrixScaleFlexOnlyWithoutGrid(componentContent) ||
    (libBlob.length > 0 && matrixScaleFlexOnlyWithoutGrid(libBlob))
  );
}

function fileHasFlexInOneGridCell(content: string): boolean {
  const hasGridColumn =
    /gridColumn\s*[:=]|gridColumn:\s*\d|gridColumn:\s*`/.test(content) ||
    /style=\{\{[^}]*gridColumn/.test(content);
  const hasFlexSpread =
    /justify-around|justify-between|justifyContent:\s*['"]space-between['"]|justifyContent:\s*['"]space-around['"]/.test(
      content,
    );
  return hasGridColumn && hasFlexSpread;
}

export function scanFlexInOneGridCellIssues(projectRoot: string): string[] {
  const issues: string[] = [];
  const { components, lib } = readMatrixLayoutFiles(projectRoot);

  for (const [name, content] of components) {
    if (!(MATRIX_SCALE_FILES as readonly string[]).includes(name)) continue;
    if (fileHasFlexInOneGridCell(content)) {
      issues.push(
        `Matrix flex-in-one-grid-cell: ${name} — use per-column MatrixGridRow children — see layout-cookbook/shared.md §Cell placement`,
      );
    }
  }

  for (const [rel, content] of lib) {
    if (fileHasFlexInOneGridCell(content)) {
      issues.push(
        `Matrix flex-in-one-grid-cell: lib/surveyUi/${rel} — use per-column MatrixGridRow children — see layout-cookbook/shared.md §Cell placement`,
      );
    }
  }

  return issues;
}

function fileHasBorderColorWithoutWidth(content: string): boolean {
  if (!/borderColor/.test(content)) return false;

  const parts = content.split(/borderColor\s*[:=]/);
  for (let i = 1; i < parts.length; i += 1) {
    const before = parts[i - 1]!.slice(-400);
    const after = parts[i]!.slice(0, 400);
    const context = `${before}borderColor:${after}`;
    const hasWidth =
      /borderWidth|border-2|borderStyle\s*:\s*['"]solid['"]/.test(context) ||
      /className=['"][^'"]*border-2/.test(context);
    if (!hasWidth) return true;
  }
  return false;
}

function collectMatrixBorderStyleFiles(projectRoot: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const file of collectSourceFiles(projectRoot)) {
    const basename = path.basename(file);
    const rel = path.relative(projectRoot, file);
    if (basename === 'selectionStyles.ts' || /MatrixScale\.tsx$/.test(basename) || basename === 'RatingScale.tsx') {
      out.set(rel, fs.readFileSync(file, 'utf8'));
    }
  }
  return out;
}

export function scanMatrixCellBorderStyleIssues(projectRoot: string): string[] {
  const issues: string[] = [];
  for (const [rel, content] of collectMatrixBorderStyleFiles(projectRoot)) {
    if (!/borderColor/.test(content)) continue;
    if (fileHasBorderColorWithoutWidth(content)) {
      issues.push(
        `${rel}: borderColor without borderWidth/border-2/borderStyle — unselected cells invisible on dark theme — see presentation-cookbook/dark-theme-contrast.md`,
      );
    }
  }
  return issues;
}

export function scanSliderScaleZoneIssues(projectRoot: string): string[] {
  const { components } = readMatrixLayoutFiles(projectRoot);
  const content = components.get('SliderMatrixScale.tsx');
  if (!content) return [];
  if (!/renderHeaderRow/.test(content)) return [];

  const headerHasScaleZone = /renderHeaderRow[\s\S]{0,2500}(flex:\s*1|flex-1|SliderScaleZone)/.test(content);
  const rowHasScaleZone = /statementRows\.map[\s\S]{0,4000}(flex:\s*1|flex-1|SliderScaleZone)/.test(content);

  if (!headerHasScaleZone || !rowHasScaleZone) {
    return [
      'SliderMatrixScale.tsx: header ticks and row loop must share SliderScaleZone (flex: 1 scale zone) — see layout-cookbook/slider_matrix.md',
    ];
  }
  return [];
}

function questionDispatcherContent(projectRoot: string): string {
  const rel = findFirstExisting(projectRoot, QUESTION_CANDIDATES);
  if (!rel) return '';
  return fs.readFileSync(path.join(projectRoot, rel), 'utf8');
}

export interface ScanMatrixLayoutOptions {
  /** When true, index-map anti-patterns fail instead of NEAR-MISS (HOOK 3 / verify-layout). */
  strict?: boolean;
}

export function scanMatrixLayoutIssues(
  projectRoot: string,
  options: ScanMatrixLayoutOptions = {},
): string[] {
  const strict = options.strict ?? false;
  const issues: string[] = [];
  const { components: files, lib } = readMatrixLayoutFiles(projectRoot);
  if (files.size === 0 && lib.size === 0) return issues;

  const matrixBlob = readMatrixLayoutBlob(projectRoot);
  const libBlob = [...lib.values()].join('\n');

  for (const [name, content] of files) {
    if ((MATRIX_SCALE_FILES as readonly string[]).includes(name)) {
      if (!/<select[\s>]/i.test(content) && !/<details[\s>]/i.test(content)) continue;
      issues.push(
        `${name}: matrix dropdown must use custom MatrixDropdown — no native <select> or <details>`,
      );
    }
  }

  const variants = readMatrixVariantsFromPlan(projectRoot);
  const hasBipolar = variants.some(
    (r) => r.type === 'CFM_MATRIX' && /bipolar/i.test(r.statementLayout),
  );
  const hasTranspose = variants.some((r) => /true/i.test(r.transposeTable));
  const hasRatingGraphics = variants.some(
    (r) => r.type === 'RATING_MATRIX' && /graphics/i.test(r.displayStyle),
  );
  const hasDropdownStyle = variants.some((r) => {
    if (r.type === 'CFM_MATRIX') {
      return /dropdown/i.test(r.gridLayout);
    }
    return /dropdown|selectbox/i.test(r.displayStyle);
  });

  if (hasBipolar) {
    const likert = files.get('LikertMatrixScale.tsx');
    if (likert && likertBipolarStructureWeak(likert)) {
      issues.push(
        'Matrix bipolar layout: LikertMatrixScale.tsx — 3-zone row with oppositeStatementText in right column (see layout-cookbook/cfm_matrix.md)',
      );
    }
    if (likert && likertCarouselLacksBipolar(likert)) {
      issues.push(
        `${MATRIX_NEAR_MISS_PREFIX} Matrix bipolar layout: carousel branch lacks bipolar 3-zone — see layout-cookbook/shared.md`,
      );
    }
  }

  if (hasTranspose) {
    if (!/transposeTable/.test(matrixBlob)) {
      issues.push(
        'Matrix scale components: §3c has transposeTable — branch with swapped row/col iteration (see layout-cookbook.md)',
      );
    }
  }

  if (hasRatingGraphics) {
    const csat = files.get('CsatMatrixScale.tsx') ?? '';
    if (!/CustomSliderTrack/.test(csat)) {
      issues.push(
        'CsatMatrixScale.tsx: §3c has RATING_MATRIX graphics — use CustomSliderTrack per row, not cell button grid',
      );
    }
    if (csat && ratingGraphicsUsesNumericIndex(csat)) {
      issues.push(
        'Matrix graphics value: CsatMatrixScale.tsx must use findIndex from stored column id — not typeof number or raw answer index — see layout-cookbook/rating_matrix.md',
      );
    }
    if (csat && graphicsLabelsInsideStatementLoop(csat)) {
      issues.push(
        'Matrix graphics labels: CsatMatrixScale.tsx must render GraphicsColumnLabelsRow once outside statementRows.map — see layout-cookbook/rating_matrix.md',
      );
    }
    const question = questionDispatcherContent(projectRoot);
    if (question && /RATING_MATRIX[\s\S]{0,400}SliderMatrixScale/.test(question)) {
      issues.push(
        'Question.tsx: RATING_MATRIX graphics must use CsatMatrixScale + CustomSliderTrack — not SliderMatrixScale',
      );
    }
  }

  if (hasDropdownStyle) {
    if (!/MatrixDropdown/.test(matrixBlob)) {
      issues.push(
        'Matrix components: §3c has dropdown/selectbox displayStyle — use MatrixDropdown per row',
      );
    }
  }

  const scaleValuesContent = readScaleValuesContent(projectRoot);
  const scaleValuesHasNonNa = /nonNaScaleColumns/.test(scaleValuesContent);
  const matrixScalesUseNonNa = [...files.entries()]
    .filter(([name]) => (MATRIX_SCALE_FILES as readonly string[]).includes(name))
    .some(([, content]) => /nonNaScaleColumns/.test(content));

  if (!scaleValuesHasNonNa || !matrixScalesUseNonNa) {
    issues.push(
      'Matrix label index: nonNaScaleColumns required in scaleValues.ts and matrix scale components — see layout-cookbook/shared.md',
    );
  }

  const likertContent = files.get('LikertMatrixScale.tsx') ?? '';
  const csatContent = files.get('CsatMatrixScale.tsx') ?? '';
  if (likertContent && likertMissingNonNaScaleColumns(likertContent)) {
    issues.push(
      'Matrix label index: LikertMatrixScale.tsx must use nonNaScaleColumns for gridCols/scaleColumns — see layout-cookbook/shared.md',
    );
  }
  if (likertContent && likertNaInGridLoop(likertContent)) {
    issues.push(
      'Matrix N/A trailing: LikertMatrixScale.tsx maps full scaleColumns/gridCols — use nonNaScaleColumns + trailing N/A slot — see layout-cookbook/cfm_matrix.md',
    );
  }
  if (csatContent && csatNaInGridLoop(csatContent)) {
    issues.push(
      'Matrix N/A trailing: CsatMatrixScale.tsx maps full scaleColumns — use nonNaScaleColumns + trailing N/A slot — see layout-cookbook/csat_matrix.md',
    );
  }

  const hasMultiColumnMatrix = variants.some(
    (v) => v.type !== 'SLIDER_MATRIX' && !/graphics/i.test(v.displayStyle),
  );

  for (const [name, content] of files) {
    if (!(MATRIX_SCALE_FILES as readonly string[]).includes(name)) continue;
    if (matrixFileHasAutoFitMinmax(content)) {
      const carouselVariant = variants.find(
        (v) => /carousel/i.test(v.gridLayout) && matrixScaleFileForType(v.type) === name,
      );
      if (carouselVariant) {
        pushAutoFitIssue(issues, name, carouselVariant.questionId, strict);
      } else {
        issues.push(
          `${MATRIX_NEAR_MISS_PREFIX} Matrix carousel parity: auto-fit minmax in ${name} — header/cell index map may drift — see layout-cookbook/shared.md`,
        );
      }
    }
    if (strict && hasMultiColumnMatrix && matrixScaleFlexOnlyWithoutGridOr(content, libBlob)) {
      issues.push(
        `Matrix grid skeleton: ${name} uses flex gap/justify-between without gridTemplateColumns repeat(m, 1fr) — see layout-cookbook/shared.md`,
      );
    }
    if (carouselMissingNavSignals(content)) {
      issues.push(
        `${MATRIX_NEAR_MISS_PREFIX} Matrix carousel chrome: ${name} missing row navigation/position indicator — see layout-cookbook/shared.md`,
      );
    }
  }

  if (hasBipolar && strict) {
    const likert = files.get('LikertMatrixScale.tsx');
    if (likert && bipolarGridOnlyWithoutMiddleFlex(likert)) {
      issues.push(
        'Matrix bipolar layout: LikertMatrixScale.tsx uses grid 0 0 25% without flex-1 middle zone — use 25% | flex-1 | 25% — see layout-cookbook/cfm_matrix.md',
      );
    }
  }

  if (csatContent && csatCarouselDuplicateLabels(csatContent)) {
    issues.push(
      'Matrix label index: CsatMatrixScale carousel renders col.label below emoji/star/numbered cells — see layout-cookbook/csat_matrix.md',
    );
  }
  if (csatContent && csatNaHeaderGapWhenAnchors(csatContent)) {
    issues.push(
      'Matrix N/A trailing: CsatMatrixScale scaleAnchorLabels row reserves N/A spacer without N/A header text — see layout-cookbook/csat_matrix.md',
    );
  }
  if (csatContent && numberedCellUsesColLabel(csatContent)) {
    issues.push(
      'Matrix label index: CsatMatrixScale numbered cells must use displayIdx+1 badge — not col.label in cell/carousel — see layout-cookbook/csat_matrix.md',
    );
  }
  if (
    likertContent &&
    /oppositeStatementText/.test(likertContent) &&
    !/0\s*0\s*25%|flex:\s*isBipolar/.test(likertContent)
  ) {
    issues.push(
      'Matrix bipolar layout: LikertMatrixScale.tsx references oppositeStatementText but lacks 3-zone right column — see layout-cookbook/cfm_matrix.md',
    );
  }

  for (const variant of variants) {
    if (parseTruthyFlag(variant.hasNotApplicableOption)) {
      const hasNaPattern =
        /hasNotApplicableOption/.test(matrixBlob) &&
        (/'N\/A'|Not Applicable|hasNotApplicableOption\s*&&/.test(matrixBlob));
      if (!hasNaPattern) {
        issues.push(
          `Matrix N/A trailing (${variant.questionId}): hasNA in §3c — N/A slot in header and every row — see layout-cookbook/csat_matrix.md`,
        );
      }
    }

    if (/carousel/i.test(variant.gridLayout)) {
      const scaleFile = matrixScaleFileForType(variant.type);
      const scaleContent = scaleFile ? files.get(scaleFile) ?? '' : '';
      const hasCarouselBranch =
        /carousel|Carousel|gridLayout\s*===\s*['"]carousel['"]/.test(scaleContent);
      if (!hasCarouselBranch) {
        issues.push(
          `Matrix carousel parity (${variant.questionId}): §3c gridLayout=carousel — header + cell loops share same column index map — see layout-cookbook/cfm_matrix.md`,
        );
      }
    }

    if (/numbered/i.test(variant.displayStyle) && variant.type !== 'SLIDER_MATRIX') {
      const csat = files.get('CsatMatrixScale.tsx') ?? '';
      if (numberedCellUsesColLabel(csat)) {
        issues.push(
          `Matrix label index (${variant.questionId}): numbered cells must use displayIdx+1 badge — not col.label — see layout-cookbook/csat_matrix.md`,
        );
      }
      if (
        csat &&
        /isStandardRadio/.test(csat) &&
        !/isNumbered/.test(csat) &&
        !/CsatMatrixGrid/.test(csat)
      ) {
        issues.push(
          `Matrix label index (${variant.questionId}): numbered §3c must route to grid/carousel with isNumbered — not vertical list only — see layout-cookbook/csat_matrix.md`,
        );
      }
    }
  }

  const likertContentForAnchor = files.get('LikertMatrixScale.tsx') ?? '';
  if (anchorUsesJustifyBetweenWithoutAbsolute(csatContent)) {
    issues.push(
      'Matrix label index: scaleAnchorLabels anchor row uses justify-between without position:absolute — use % anchors — see layout-cookbook/shared.md',
    );
  }
  if (anchorUsesJustifyBetweenWithoutAbsolute(likertContentForAnchor)) {
    issues.push(
      'Matrix label index: scaleAnchorLabels anchor row uses justify-between without position:absolute — use % anchors — see layout-cookbook/shared.md',
    );
  }

  return issues;
}

function dropdownLayoutHasScaleHeaderLeak(body: string): boolean {
  if (!body) return false;
  return (
    /scaleAnchorLabels/.test(body) ||
    /renderHeaderRow/.test(body) ||
    /ColumnHeaderRow/.test(body) ||
    /AnchorLabelsRow/.test(body) ||
    /MatrixHeaderRow/.test(body) ||
    /justifyContent:\s*['"]space-between['"]/.test(body) ||
    /justify-between/.test(body)
  );
}

/** FAIL when matrix dropdown layout functions render grid scale header bands. */
export function scanMatrixDropdownHeaderLeak(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const dropdownVariants = variants.filter((v) =>
    isMatrixDropdownVariant(v as MatrixVariantRow),
  );
  if (!dropdownVariants.length) return [];

  const issues: string[] = [];
  const { components: files } = readMatrixLayoutFiles(projectRoot);

  const hasCsatRatingDropdown = dropdownVariants.some(
    (v) => v.type === 'CSAT_MATRIX' || v.type === 'RATING_MATRIX',
  );
  const hasCfmDropdown = dropdownVariants.some((v) => v.type === 'CFM_MATRIX');

  if (hasCsatRatingDropdown) {
    const csat = files.get('CsatMatrixScale.tsx') ?? '';
    const verticalBody = extractFunctionBody(csat, 'CsatMatrixVerticalList');
    if (dropdownLayoutHasScaleHeaderLeak(verticalBody)) {
      issues.push(
        'Matrix dropdown (CSAT/RATING): CsatMatrixVerticalList must not render scale header row — labels live inside MatrixDropdown options — see layout-audit.md pitfall ledger',
      );
    }
  }

  if (hasCfmDropdown) {
    const likert = files.get('LikertMatrixScale.tsx') ?? '';
    const dropdownBody = extractFunctionBody(likert, 'LikertMatrixDropdownLayout');
    if (dropdownLayoutHasScaleHeaderLeak(dropdownBody)) {
      issues.push(
        'Matrix dropdown (CFM): LikertMatrixDropdownLayout must not render scale header row — labels live inside MatrixDropdown options — see layout-audit.md pitfall ledger',
      );
    }
  }

  return issues;
}

export function scanMatrixLayoutIssuesStrict(projectRoot: string): string[] {
  return [
    ...scanMatrixLayoutIssues(projectRoot, { strict: true }),
    ...scanFlexInOneGridCellIssues(projectRoot),
    ...scanBipolarSliderIssues(projectRoot),
    ...scanMatrixDropdownHeaderLeak(projectRoot),
  ];
}

/**
 * Fail-closed guard for the §3c enforcement chain.
 *
 * The structural matrix checks (bipolar / transpose / graphics / dropdown) are all derived
 * from `implementation_plan.md` §3c. If matrix component files exist but §3c is empty, those
 * checks silently no-op. At the Phase-6 gate (full `verify-build`, run AFTER `sync-from-fetch`)
 * an empty §3c means the agent skipped `sync-from-fetch` or never populated the inventory — so
 * the layout checks never ran. Surface that as a blocking issue instead of passing silently.
 *
 * Only meaningful post-sync: do NOT call this from `--fast` (which legitimately runs before
 * `sync-from-fetch`).
 */
export function scanMatrixInventoryGap(projectRoot: string): string[] {
  const files = readMatrixFileContents(projectRoot);
  if (files.size === 0) return [];

  const variants = readMatrixVariantsFromPlan(projectRoot);
  if (variants.length > 0) return [];

  return [
    'Matrix component files exist but implementation_plan.md §3c is empty — run `npx cfm-sdk sync-from-fetch` and fill §3c, then re-verify (matrix layout checks depend on §3c). See layout-cookbook.md.',
  ];
}

export function scanMatrixStatementIssues(projectRoot: string): string[] {
  const issues: string[] = [];

  for (const file of collectSourceFiles(projectRoot)) {
    const basename = path.basename(file);
    if (!(MATRIX_SCALE_FILES as readonly string[]).includes(basename)) continue;

    const content = fs.readFileSync(file, 'utf8');
    const rel = path.relative(projectRoot, file);
    if (!/shouldShowMatrixStatementLabels/.test(content)) {
      issues.push(
        `${rel}: must call shouldShowMatrixStatementLabels(question) for single- vs multi-row statement labels`,
      );
    }
  }

  return issues;
}

export function readMcqOtherFlagFromPlan(projectRoot: string): boolean | null {
  const planPath = path.join(projectRoot, 'implementation_plan.md');
  if (!fs.existsSync(planPath)) return null;

  const content = fs.readFileSync(planPath, 'utf8');
  const match = content.match(/\*\*MCQ Other option in fetch:\*\*\s*(yes|no)/i);
  if (!match) return null;
  return match[1].toLowerCase() === 'yes';
}

export function readMcqOtherPickersFlagFromPlan(projectRoot: string): boolean | null {
  const planPath = path.join(projectRoot, 'implementation_plan.md');
  if (!fs.existsSync(planPath)) return null;

  const content = fs.readFileSync(planPath, 'utf8');
  const match = content.match(/\*\*MCQ Other phone\/postal\/date pickers:\*\*\s*(yes|no)/i);
  if (!match) return null;
  return match[1].toLowerCase() === 'yes';
}

function stripLineComments(content: string): string {
  return content
    .split('\n')
    .map((line) => line.replace(/\/\/.*$/, '').replace(/\/\*[\s\S]*?\*\//g, ''))
    .join('\n');
}

function findComponentFile(projectRoot: string, basename: string): string | null {
  for (const file of collectSourceFiles(projectRoot)) {
    if (path.basename(file) === basename) return file;
  }
  return null;
}

function readPackageJsonContent(projectRoot: string): string {
  const pkgPath = path.join(projectRoot, 'package.json');
  if (!fs.existsSync(pkgPath)) return '';
  return fs.readFileSync(pkgPath, 'utf8');
}

/** FAIL when CFM component branches on displayStyle (field does not exist on type). */
export function scanLikertDisplayStyleMisuse(projectRoot: string): string[] {
  const likertRel = findFirstExisting(projectRoot, [
    'src/components/LikertMatrixScale.tsx',
    'components/LikertMatrixScale.tsx',
    'app/components/LikertMatrixScale.tsx',
  ]);
  if (!likertRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, likertRel), 'utf8');
  const stripped = stripLineComments(content);
  if (/\bdisplayStyle\b/.test(stripped)) {
    return [
      `${likertRel}: CFM_MATRIX has no displayStyle — use question.gridLayout === 'dropdown' — see field-binding-map.md`,
    ];
  }
  return [];
}

/** FAIL when CsatStarIcons uses NPS-style half-fill anti-pattern. */
export function scanCsatStarIconAntiPattern(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const hasStar =
    variants.some((v) => v.type === 'CSAT_MATRIX' && /star/i.test(v.displayStyle)) ||
    variants.length === 0;

  if (!hasStar) return [];

  const iconsPath = findComponentFile(projectRoot, 'icons.tsx');
  if (!iconsPath) return [];

  const content = fs.readFileSync(iconsPath, 'utf8');
  if (/index\s*<\s*Math\.ceil|ceil\s*\(\s*total\s*\/\s*2\s*\)/.test(content)) {
    return [
      `${path.relative(projectRoot, iconsPath)}: CsatStarIcons must use filled === selected only — not NPS half-fill — see client-lib-folder.md`,
    ];
  }
  return [];
}

function csatMatrixStarHasNpsStrip(content: string): boolean {
  return /CsatStarIcons[^>]*total\s*=\s*\{\s*(m|scaleColumns)/.test(content);
}

function csatMatrixStarHasPerCellStrip(content: string): boolean {
  return /CsatStarIcons[^>]*total\s*=\s*\{1\}/.test(content);
}

function csatMatrixStarBypassesGrid(content: string): boolean {
  const starIfReturn = content.match(
    /if\s*\(\s*displayStyle\s*===\s*['"]star['"]\s*\)\s*return\s+([^;}\n]+)/,
  );
  if (
    starIfReturn &&
    /CsatStarIcons/.test(starIfReturn[1]) &&
    !/scaleColumns\.map/.test(starIfReturn[1])
  ) {
    return true;
  }

  const renderRowBody = extractFunctionBody(content, 'renderRow');
  if (renderRowBody) {
    const starBranch = renderRowBody.match(
      /if\s*\(\s*displayStyle\s*===\s*['"]star['"]\s*\)\s*return\s+([^;}\n]+)/,
    );
    if (
      starBranch &&
      /CsatStarIcons/.test(starBranch[1]) &&
      !/scaleColumns\.map/.test(starBranch[1])
    ) {
      return true;
    }
  }

  return false;
}

function csatStarIconsInvisibleEmpty(iconsContent: string): boolean {
  const emptyBlock = iconsContent.match(/empty:[\s\S]{0,400}/)?.[0] ?? '';
  if (!emptyBlock) return false;
  return (
    /fill:\s*['"]none['"]/.test(emptyBlock) &&
    /FaStar/.test(emptyBlock) &&
    !/FaRegStar/.test(emptyBlock)
  );
}

/** FAIL when CSAT matrix star uses NPS flex strip, per-cell total={1}, grid bypass, or invisible empty stars. */
export function scanCsatMatrixStarGridIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const hasStar =
    variants.some((v) => v.type === 'CSAT_MATRIX' && /star/i.test(v.displayStyle)) ||
    variants.length === 0;

  if (!hasStar) return [];

  const csatRel = findFirstExisting(projectRoot, [
    'src/components/CsatMatrixScale.tsx',
    'components/CsatMatrixScale.tsx',
  ]);
  if (!csatRel) return [];

  const content = stripLineComments(fs.readFileSync(path.join(projectRoot, csatRel), 'utf8'));
  const issues: string[] = [];

  if (csatMatrixStarHasNpsStrip(content)) {
    issues.push(
      `${csatRel}: CSAT matrix star must use one star per scaleColumns.map cell — not CsatStarIcons total={m} flex strip — see 04-csat.md §Implementation Recipe — star`,
    );
  }

  if (csatMatrixStarHasPerCellStrip(content)) {
    issues.push(
      `${csatRel}: CSAT matrix star must not use CsatStarIcons total={1} per cell — use CsatStarIcons.filled/.empty per column — see client-lib-folder.md`,
    );
  }

  if (csatMatrixStarBypassesGrid(content)) {
    issues.push(
      `${csatRel}: CSAT matrix star must not early-return a CsatStarIcons strip — use default grid renderRow with scaleColumns.map — see 04-csat.md`,
    );
  }

  const iconsPath = findComponentFile(projectRoot, 'icons.tsx');
  if (iconsPath) {
    const iconsContent = fs.readFileSync(iconsPath, 'utf8');
    if (csatStarIconsInvisibleEmpty(iconsContent)) {
      issues.push(
        `${path.relative(projectRoot, iconsPath)}: CSAT matrix empty star must be visible (FaRegStar or stroked outline) — not FaStar with fill='none' — see client-lib-folder.md`,
      );
    }
  }

  return issues;
}

/** FAIL when fetch requires MCQ Other pickers but client uses plain inputs. */
export function scanMcqOtherPickerIssues(projectRoot: string): string[] {
  if (readMcqOtherPickersFlagFromPlan(projectRoot) !== true) return [];

  const issues: string[] = [];
  const countryRel = findFirstExisting(projectRoot, [
    'src/components/McqOtherCountrySelect.tsx',
    'components/McqOtherCountrySelect.tsx',
  ]);
  const dateRel = findFirstExisting(projectRoot, [
    'src/components/McqOtherDatePicker.tsx',
    'components/McqOtherDatePicker.tsx',
  ]);
  const inputRel = findFirstExisting(projectRoot, [
    'src/components/McqOtherInput.tsx',
    'components/McqOtherInput.tsx',
  ]);

  if (!countryRel) {
    issues.push(
      'McqOtherCountrySelect.tsx missing — survey has PHONE_NUMBER/POSTAL_CODE Other — see 21-mcq-other-input.md',
    );
  }
  if (!dateRel) {
    issues.push('McqOtherDatePicker.tsx missing — survey has DATE Other — see 21-mcq-other-input.md');
  }

  const pkg = readPackageJsonContent(projectRoot);
  if (!/react-international-phone/.test(pkg)) {
    issues.push('package.json: missing react-international-phone — required for MCQ Other phone/postal');
  }
  if (!/react-datepicker/.test(pkg)) {
    issues.push('package.json: missing react-datepicker — required for MCQ Other date');
  }

  if (inputRel) {
    const inputContent = fs.readFileSync(path.join(projectRoot, inputRel), 'utf8');
    if (/type\s*=\s*["']date["']/i.test(inputContent)) {
      issues.push(`${inputRel}: DATE Other must use McqOtherDatePicker — not type="date"`);
    }
    if (/<select[\s>]/i.test(inputContent) && /country/i.test(inputContent)) {
      issues.push(
        `${inputRel}: use McqOtherCountrySelect for country — not plain <select> — see layout-cookbook/mcq.md`,
      );
    }
    if (
      /requires picker dependencies|not configured for this survey/i.test(inputContent)
    ) {
      issues.push(
        `${inputRel}: MCQ Other pickers required — remove stub placeholder; wire McqOtherCountrySelect + McqOtherDatePicker — see 21-mcq-other-input.md`,
      );
    }
  }

  if (countryRel) {
    const countryContent = fs.readFileSync(path.join(projectRoot, countryRel), 'utf8');

    if (!/FlagImage/.test(countryContent)) {
      issues.push(
        `${countryRel}: must render FlagImage in country picker — see 21-mcq-other-input.md`,
      );
    }
    if (!/defaultCountries/.test(countryContent) || !/parseCountry/.test(countryContent)) {
      issues.push(
        `${countryRel}: must filter defaultCountries via parseCountry — see 21-mcq-other-input.md`,
      );
    }
    if (
      !/iso2\.toUpperCase\(\)/.test(countryContent) &&
      !/normalizeIso|normalizeCountryCode/i.test(countryContent)
    ) {
      issues.push(
        `${countryRel}: country filter must normalize iso2 case (iso2.toUpperCase) — API countryCodes are uppercase — see 21-mcq-other-input.md`,
      );
    }
    if (/parsed\.iso2|parsed\.local/.test(countryContent)) {
      issues.push(
        `${countryRel}: parseMcqOtherCountryValue returns countryCode/value — not iso2/local — see 21-mcq-other-input.md`,
      );
    }
    if (!/countryCode/.test(countryContent)) {
      issues.push(
        `${countryRel}: must read parseMcqOtherCountryValue.countryCode — see 21-mcq-other-input.md`,
      );
    }
    if (!/countries\.length|allowedSet|allowedIso|allowedCountryCodes\.length/.test(countryContent)) {
      issues.push(
        `${countryRel}: NEAR-MISS — guard empty allowed country list (countries.length) — blank trigger when filter returns 0 — see 21-mcq-other-input.md`,
      );
    }
  }

  return issues;
}

function parseAnchorLabelCount(value: string | undefined): number {
  if (!value || value.trim() === '—') return 0;
  const n = parseInt(value.trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function surveyPlanHasType(projectRoot: string, type: string): boolean {
  const planPath = path.join(projectRoot, 'implementation_plan.md');
  if (!fs.existsSync(planPath)) return false;
  const content = fs.readFileSync(planPath, 'utf8');
  const section = content.split('## 3b.')[1]?.split('##')[0] ?? content;
  return (
    new RegExp(`\\|\\s*${type}\\s*\\|`).test(section) ||
    section.includes(`\`${type}\``) ||
    section.includes(type)
  );
}

function extractLikertCarouselBlock(content: string): string {
  const fn = content.match(
    /function\s+LikertMatrixCarousel\w*[\s\S]*?(?=\nfunction\s|\nexport\s|$)/,
  );
  return fn?.[0] ?? '';
}

function likertCarouselHasDotNav(block: string): boolean {
  if (/carousel-dot|role=['"]tablist['"]/.test(block)) return true;
  return (
    /statementRows\.map/.test(block) &&
    /borderRadius:\s*['"]50%|borderRadius:\s*'50%'/.test(block)
  );
}

function likertCarouselHasChevronNav(block: string): boolean {
  return /Prev|Next|polyline points|activeCarouselIndex|setActiveCarouselIndex/.test(block);
}

/** FAIL when CFM carousel layout is structurally weak (duplicate stmt risk, no labels, no nav). */
function likertCarouselLayoutWeak(content: string): boolean {
  const block = extractLikertCarouselBlock(content);
  if (!block) return true;

  if (!/scaleColumns\.map/.test(block)) return true;

  const hasPerColLabel = /col\.label|\.label/.test(block);
  const hasHeaderBand =
    /renderCarouselHeader|CarouselHeaderRow|renderHeaderRow|MatrixHeaderRow|scaleAnchorLabels/.test(
      block,
    );
  if (!hasPerColLabel && !hasHeaderBand) return true;

  if (/renderRow\s*\(/.test(block)) return true;

  if (
    /shouldShowMatrixStatementLabels/.test(block) &&
    !/gridLayout\s*!==\s*['"]carousel['"]|gridLayout\s*===\s*['"]carousel['"]\s*\?\s*false|!isCarousel/.test(
      block,
    )
  ) {
    return true;
  }

  if (
    /(?:^|[^a-z])0\s+0\s+180px|['"]0\s+0\s+180px['"]/.test(block) &&
    /statementText|row\.statementText/.test(block)
  ) {
    return true;
  }

  if (!likertCarouselHasDotNav(block) && !likertCarouselHasChevronNav(block)) {
    return true;
  }

  return false;
}

/** FAIL when heatmap click handler lacks maxClicksAllowed guard. */
export function scanHeatmapMaxClicksIssues(projectRoot: string): string[] {
  const heatmapRel = findFirstExisting(projectRoot, [
    'src/components/HeatmapScale.tsx',
    'components/HeatmapScale.tsx',
  ]);
  if (!heatmapRel && !surveyPlanHasType(projectRoot, 'HEATMAP')) return [];

  if (!heatmapRel) {
    return ['HeatmapScale.tsx missing — survey has HEATMAP — see layout-cookbook/heatmap.md'];
  }

  const content = fs.readFileSync(path.join(projectRoot, heatmapRel), 'utf8');
  const issues: string[] = [];
  if (!/maxClicksAllowed/.test(content)) {
    issues.push(`${heatmapRel}: must enforce question.maxClicksAllowed — see layout-cookbook/heatmap.md`);
  } else if (
    !/spots\.length\s*<\s*question\.maxClicksAllowed|maxClicksAllowed.*spots\.length|spots\.length\s*>=\s*question\.maxClicksAllowed/.test(
      content,
    )
  ) {
    issues.push(
      `${heatmapRel}: handleClick must guard new spots with spots.length < question.maxClicksAllowed — see layout-cookbook/heatmap.md`,
    );
  }
  if (/maxClicksAllowed/.test(content) && !/question\.maxClicksAllowed/.test(content)) {
    issues.push(
      `${heatmapRel}: read maxClicksAllowed from question — not hardcoded — see agent-per-type-verification/heatmap.md`,
    );
  }
  return issues;
}

function parseNonNaM(value: string | undefined): number {
  if (!value || value.trim() === '—') return 0;
  const n = parseInt(value.trim(), 10);
  return Number.isFinite(n) ? n : 0;
}

/** Extract isBipolarSlider branch containing CustomSliderTrack (LikertMatrixScale). */
function extractBipolarSliderBlock(content: string): string {
  const match = content.match(
    /isBipolarSlider\s*\?[\s\S]{0,3000}CustomSliderTrack[\s\S]{0,2000}/,
  );
  return match?.[0] ?? '';
}

function bipolarSliderHasTicksEqualsTwo(content: string): boolean {
  const block = extractBipolarSliderBlock(content) || content;
  return /ticks=\{\s*2\s*\}/.test(block);
}

function bipolarSliderTicksTiedToColumnCount(content: string): boolean {
  const block = extractBipolarSliderBlock(content) || content;
  return /ticks=\{[^}]*(?:nonNaCols|nonNaM|colCount)/.test(block);
}

function bipolarSliderHasDiscreteMode(content: string): boolean {
  const block = extractBipolarSliderBlock(content) || content;
  return /positionMode=["']discrete["']|positionMode=\{['"]discrete['"]\}/.test(block);
}

/** FAIL when bipolar anchor header uses bespoke 100/(2*numCols) instead of columnPercent(i, n). */
function bipolarAnchorUsesBespokeFormula(content: string): boolean {
  const headerBody = extractFunctionBody(content, 'renderHeader') || content;
  const anchorBlock =
    headerBody.match(/(?:labels\.map|scaleAnchorLabels\.map)[\s\S]{0,1500}/)?.[0] ?? '';
  const region = anchorBlock || headerBody;
  if (!/scaleAnchorLabels|labels\.map/.test(region)) return false;
  const hasBespoke = /100\s*\/\s*\(\s*2\s*\*/.test(region) || /2\s*\*\s*numCols/.test(region);
  if (!hasBespoke) return false;
  return !/columnPercent/.test(region);
}

/** FAIL when §3c CFM bipolar SLIDER (or nonNaM > 7) lacks CustomSliderTrack center branch. */
export function scanBipolarSliderIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const needsSlider = variants.some((v) => {
    if (v.type !== 'CFM_MATRIX' || !/bipolar/i.test(v.statementLayout)) return false;
    if (/SLIDER/i.test(v.matrixFormat ?? '')) return true;
    if (parseNonNaM(v.nonNaM) > 7) return true;
    if (/matrixFormat=SLIDER/i.test(v.branchOn ?? '')) return true;
    return false;
  });
  if (!needsSlider) return [];

  const likertRel = findFirstExisting(projectRoot, [
    'src/components/LikertMatrixScale.tsx',
    'components/LikertMatrixScale.tsx',
  ]);
  if (!likertRel) {
    return ['LikertMatrixScale.tsx missing — CFM bipolar SLIDER in §3c — see cfm_matrix.md'];
  }

  const content = fs.readFileSync(path.join(projectRoot, likertRel), 'utf8');
  const issues: string[] = [];
  if (!/CustomSliderTrack/.test(content)) {
    issues.push(
      `${likertRel}: §3c bipolar SLIDER — render CustomSliderTrack in center zone (not radio grid) — see cfm_matrix.md`,
    );
  }
  const hasSliderBranch =
    /isBipolarSlider/.test(content) ||
    (/matrixFormat/.test(content) && /SLIDER/.test(content)) ||
    /nonNaM\s*>\s*7|nonNaCols\.length\s*>\s*7/.test(content);
  if (!hasSliderBranch) {
    issues.push(
      `${likertRel}: branch bipolar slider vs radio (isBipolarSlider / matrixFormat / nonNaM > 7) — see cfm_matrix.md`,
    );
  }
  if (/CustomSliderTrack/.test(content)) {
    if (bipolarSliderHasTicksEqualsTwo(content)) {
      issues.push(
        `${likertRel}: bipolar SLIDER ticks must be nonNaCols.length (m), not 2 — see cfm_matrix.md`,
      );
    }
    if (!bipolarSliderTicksTiedToColumnCount(content)) {
      issues.push(
        `${likertRel}: bipolar SLIDER ticks must equal column count (ticks={nonNaCols.length} or ticks={nonNaM}) — see cfm_matrix.md`,
      );
    }
    if (!bipolarSliderHasDiscreteMode(content)) {
      issues.push(
        `${likertRel}: bipolar SLIDER requires positionMode="discrete" on CustomSliderTrack — see cfm_matrix.md`,
      );
    }
  }
  return issues;
}

/** FAIL when §3c CFM bipolar has anchors but Likert lacks scaleAnchorLabels header wiring. */
export function scanBipolarAnchorHeaderIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const needsHeader = variants.some(
    (v) =>
      v.type === 'CFM_MATRIX' &&
      /bipolar/i.test(v.statementLayout) &&
      parseAnchorLabelCount(v.scaleAnchorLabelCount) > 0,
  );
  if (!needsHeader) return [];

  const likertRel = findFirstExisting(projectRoot, [
    'src/components/LikertMatrixScale.tsx',
    'components/LikertMatrixScale.tsx',
  ]);
  if (!likertRel) {
    return ['LikertMatrixScale.tsx missing — CFM bipolar with anchors in §3c — see cfm_matrix.md'];
  }

  const content = fs.readFileSync(path.join(projectRoot, likertRel), 'utf8');
  const issues: string[] = [];
  if (!/scaleAnchorLabels/.test(content)) {
    issues.push(
      `${likertRel}: §3c bipolar with anchors — render scaleAnchorLabels header row with 25% spacers — see cfm_matrix.md`,
    );
  }
  if (anchorUsesJustifyBetweenWithoutAbsolute(content)) {
    issues.push(
      `${likertRel}: bipolar anchor row must use position:absolute + % — not justify-between — see scale-position-math.md`,
    );
  }
  return issues;
}

/** FAIL when §3c CFM bipolar uses likert-grid header or lacks flex 25% spacers on anchor header. */
export function scanBipolarHeaderRegimeIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const needsHeader = variants.some(
    (v) =>
      v.type === 'CFM_MATRIX' &&
      /bipolar/i.test(v.statementLayout) &&
      parseAnchorLabelCount(v.scaleAnchorLabelCount) > 0,
  );
  if (!needsHeader) return [];

  const likertRel = findFirstExisting(projectRoot, [
    'src/components/LikertMatrixScale.tsx',
    'components/LikertMatrixScale.tsx',
  ]);
  if (!likertRel) return [];

  const content = stripLineComments(
    fs.readFileSync(path.join(projectRoot, likertRel), 'utf8'),
  );
  const issues: string[] = [];

  if (bipolarAnchorHeaderUsesLikertGrid(content)) {
    issues.push(
      `${likertRel}: CFM bipolar anchor header must use flex 25%|flex-1|25% — not likertGridColumns/gridCols — see shared.md §Bipolar regime`,
    );
  }

  if (bipolarAnchorHeaderMissingFlexSpacers(content)) {
    issues.push(
      `${likertRel}: CFM bipolar with anchors — header must use flex:'0 0 25%' spacers — see cfm_matrix.md`,
    );
  }

  if (bipolarRowHasZoneGapWithoutHeaderMatch(content)) {
    issues.push(
      `${MATRIX_NEAR_MISS_PREFIX} ${likertRel}: CFM bipolar rows use gap-* between zones — shifts center zone vs header — remove zone gap`,
    );
  }

  if (bipolarUsesW14Only(content)) {
    issues.push(
      `${MATRIX_NEAR_MISS_PREFIX} ${likertRel}: CFM bipolar uses w-1/4 only — prefer inline flex:'0 0 25%' on header and rows`,
    );
  }

  if (bipolarAnchorUsesBespokeFormula(content)) {
    issues.push(
      `${likertRel}: bipolar anchor header must use columnPercent(i, n) — not bespoke 100/(2*numCols) — see scale-position-math.md §Bipolar SLIDER`,
    );
  }

  return issues;
}

/** FAIL when §3c CFM carousel lacks Sprinklr-parity layout (dedicated component, labels, nav). */
export function scanCarouselScaleHeaderIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const hasCfmCarousel = variants.some(
    (v) => v.type === 'CFM_MATRIX' && /carousel/i.test(v.gridLayout),
  );
  if (!hasCfmCarousel) return [];

  const likertRel = findFirstExisting(projectRoot, [
    'src/components/LikertMatrixScale.tsx',
    'components/LikertMatrixScale.tsx',
  ]);
  if (!likertRel) {
    return ['LikertMatrixScale.tsx missing — CFM carousel in §3c — see cfm_matrix.md § carousel'];
  }

  const content = fs.readFileSync(path.join(projectRoot, likertRel), 'utf8');
  const block = extractLikertCarouselBlock(content);
  const issues: string[] = [];

  if (!block) {
    issues.push(
      `${likertRel}: §3c CFM carousel — use dedicated LikertMatrixCarouselLayout (not inline gridLayout branch only) — see cfm_matrix.md`,
    );
    return issues;
  }

  if (likertCarouselLayoutWeak(content)) {
    if (!/scaleColumns\.map/.test(block)) {
      issues.push(
        `${likertRel}: CFM carousel — scaleColumns.map required in LikertMatrixCarouselLayout — see cfm_matrix.md`,
      );
    } else if (!/col\.label|\.label/.test(block) && !/scaleAnchorLabels|MatrixHeaderRow/.test(block)) {
      issues.push(
        `${likertRel}: CFM carousel — per-column col.label + radio or anchor header band — see cfm_matrix.md`,
      );
    } else if (/renderRow\s*\(/.test(block)) {
      issues.push(
        `${likertRel}: CFM carousel — do not reuse standard renderRow; dedicated carousel component only — see cfm_matrix.md`,
      );
    } else if (
      /shouldShowMatrixStatementLabels/.test(block) &&
      !/gridLayout\s*!==\s*['"]carousel['"]|!isCarousel/.test(block)
    ) {
      issues.push(
        `${likertRel}: CFM carousel — never use shouldShowMatrixStatementLabels for row stmt column — see cfm_matrix.md`,
      );
    } else if (
      /(?:^|[^a-z])0\s+0\s+180px|['"]0\s+0\s+180px['"]/.test(block) &&
      /statementText|row\.statementText/.test(block)
    ) {
      issues.push(
        `${likertRel}: CFM carousel — statement once in card hero (no 180px stmt column + centered statementText) — see cfm_matrix.md`,
      );
    } else if (!likertCarouselHasDotNav(block) && !likertCarouselHasChevronNav(block)) {
      issues.push(
        `${likertRel}: CFM carousel — chevrons + dot pagination required — see cfm_matrix.md`,
      );
    }
  }

  return issues;
}

/** Extract RATING/CSAT graphics branch block from CsatMatrixScale. */
function extractRatingGraphicsBlock(content: string): string {
  const match = content.match(
    /(?:isGraphics|displayStyle\s*===\s*['"]graphics['"])[\s\S]{0,12000}/,
  );
  return match?.[0] ?? '';
}

function ratingGraphicsHasTicksEqualsTwo(content: string): boolean {
  const block = extractRatingGraphicsBlock(content) || content;
  return /ticks=\{\s*2\s*\}/.test(block);
}

function ratingGraphicsTicksTiedToColumnCount(content: string): boolean {
  const block = extractRatingGraphicsBlock(content) || content;
  if (!/CustomSliderTrack/.test(block)) return false;
  return /ticks=\{[^}]*(?:scaleColumns|nonNa)/.test(block);
}

function ratingGraphicsHasScaleZone(content: string): boolean {
  return (
    /GraphicsScaleZone/.test(content) ||
    (/graphicsScaleInsetPercent/.test(content) && /graphicsLabelPercent/.test(content))
  );
}

function ratingGraphicsUsesDiscreteWithoutZone(content: string): boolean {
  const block = extractRatingGraphicsBlock(content) || content;
  if (!/CustomSliderTrack/.test(block)) return false;
  const hasDiscrete = /positionMode=["']discrete["']|positionMode=\{['"]discrete['"]\}/.test(block);
  if (!hasDiscrete) return false;
  return !ratingGraphicsHasScaleZone(content);
}

/** FAIL when RATING graphics uses flex distribute for labels instead of columnPercent map. */
export function scanRatingGraphicsIndexMapIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const hasGraphics = variants.some(
    (v) => v.type === 'RATING_MATRIX' && /graphics/i.test(v.displayStyle),
  );
  if (!hasGraphics) return [];

  const csatRel = findFirstExisting(projectRoot, [
    'src/components/CsatMatrixScale.tsx',
    'components/CsatMatrixScale.tsx',
  ]);
  if (!csatRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, csatRel), 'utf8');
  const issues: string[] = [];
  const graphicsBlock =
    extractRatingGraphicsBlock(content) ||
    (content.match(/displayStyle\s*===\s*['"]graphics['"][\\s\\S]{0,6000}/)?.[0] ?? '');

  if (graphicsBlock) {
    if (
      (/justify-around|justify-between|justifyContent:\s*['"]space-between['"]/.test(graphicsBlock) ||
        /justifyContent:\s*['"]space-around['"]/.test(graphicsBlock)) &&
      !/columnPercent|graphicsLabelPercent|0\.5\s*\/\s*m|translateX\(-50%\)/.test(graphicsBlock)
    ) {
      issues.push(
        `${csatRel}: RATING graphics labels must use graphicsLabelPercent/columnPercent + absolute % — not flex distribute — see rating_matrix.md (Q22)`,
      );
    }
  }

  if (/CustomSliderTrack/.test(content)) {
    if (ratingGraphicsHasTicksEqualsTwo(content)) {
      issues.push(
        `${csatRel}: RATING graphics ticks must be scaleColumns.length (m), not 2 — see rating_matrix.md`,
      );
    }
    if (!ratingGraphicsTicksTiedToColumnCount(content)) {
      issues.push(
        `${csatRel}: RATING graphics ticks must equal column count (ticks={scaleColumns.length}) — see rating_matrix.md`,
      );
    }
    if (!ratingGraphicsHasScaleZone(content)) {
      issues.push(
        `${csatRel}: RATING graphics must use GraphicsScaleZone + graphicsScaleInsetPercent/graphicsLabelPercent — see rating_matrix.md`,
      );
    }
    if (ratingGraphicsUsesDiscreteWithoutZone(content)) {
      issues.push(
        `${csatRel}: RATING graphics inside GraphicsScaleZone requires positionMode="continuous" — not discrete on full width — see rating_matrix.md`,
      );
    }
  }

  if (/SliderMatrixScale/.test(content)) {
    issues.push(
      `${csatRel}: RATING_MATRIX graphics must use CustomSliderTrack in CsatMatrixScale — not SliderMatrixScale — see rating_matrix.md`,
    );
  }

  const trackRel = findFirstExisting(projectRoot, [
    'src/components/CustomSliderTrack.tsx',
    'components/CustomSliderTrack.tsx',
  ]);
  if (trackRel) {
    const track = fs.readFileSync(path.join(projectRoot, trackRel), 'utf8');
    if (/tickLabels[\s\S]{0,400}<span|tickLabels[\s\S]{0,400}<div/.test(track)) {
      issues.push(
        `${trackRel}: tickLabels are tooltip-only — do not render visible label DOM on track — see custom-slider-track.md`,
      );
    }
  }

  return issues;
}

/** FAIL when CSAT/CFM matrix renders both anchor and column header rows without XOR guard. */
function matrixScaleDuplicateHeaderPattern(content: string): boolean {
  if (/MatrixHeaderRow/.test(content)) return false;
  if (/\buseAnchors\b/.test(content)) return false;
  if (/scaleAnchorLabels\?\.length\s*>\s*0\s*\?/.test(content)) return false;
  if (/scaleAnchorLabels\.length\s*>\s*0\s*\?/.test(content)) return false;

  if (/renderAnchorHeader/.test(content) && /renderColumnHeader/.test(content)) {
    return true;
  }

  // RATING graphics intentionally stacks optional anchor + GraphicsColumnLabelsRow (different content).
  if (/GraphicsColumnLabelsRow/.test(content) && /displayStyle\s*===\s*['"]graphics['"]/.test(content)) {
    return false;
  }

  const hasAnchorPath =
    /scaleAnchorLabels/.test(content) &&
    (/AnchorLabelsRow|columnPercent\(/.test(content) ||
      /position:\s*['"]absolute['"]/.test(content) ||
      /position:\s*absolute/.test(content));

  const hasColumnHeaderGrid =
    (/showColumnHeaders/.test(content) || /scaleColumns\.map/.test(content)) &&
    (/ColumnHeaderRow/.test(content) ||
      (/col\.label/.test(content) &&
        /gridTemplateColumns|MatrixGridRow|repeat\(/.test(content)));

  return hasAnchorPath && hasColumnHeaderGrid;
}

export function scanMatrixDuplicateHeaderIssues(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const needsCheck = variants.some(
    (v) =>
      v.type === 'CSAT_MATRIX' ||
      v.type === 'CFM_MATRIX' ||
      (v.type === 'RATING_MATRIX' && !/graphics/i.test(v.displayStyle)),
  );
  if (!needsCheck) return [];

  const issues: string[] = [];
  for (const rel of [
    'src/components/CsatMatrixScale.tsx',
    'components/CsatMatrixScale.tsx',
    'src/components/LikertMatrixScale.tsx',
    'components/LikertMatrixScale.tsx',
  ]) {
    const abs = path.join(projectRoot, rel);
    if (!fs.existsSync(abs)) continue;
    const content = fs.readFileSync(abs, 'utf8');
    if (matrixScaleDuplicateHeaderPattern(content)) {
      issues.push(
        `${rel}: duplicate matrix header row — anchor XOR column header only (MatrixHeaderRow / if-else) — see layout-cookbook/shared.md`,
      );
    }
  }
  return issues;
}

/** FAIL when SLIDER_MATRIX has both anchors and ticks in one header layer. */
export function scanSliderDualHeaderIssues(projectRoot: string): string[] {
  if (!surveyPlanHasType(projectRoot, 'SLIDER_MATRIX')) return [];

  const sliderRel = findFirstExisting(projectRoot, [
    'src/components/SliderMatrixScale.tsx',
    'components/SliderMatrixScale.tsx',
  ]);
  if (!sliderRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, sliderRel), 'utf8');
  const hasAnchors = /scaleAnchorLabels/.test(content);
  const hasTicks = /ticks\.marks|tickValues|renderHeaderRow/.test(content);
  if (!hasAnchors || !hasTicks) return [];

  if (/bottom-0[\s\S]{0,800}top-0|top-0[\s\S]{0,800}bottom-0/.test(content)) {
    return [
      `${sliderRel}: dual header (anchors + ticks) must use two stacked rows — not bottom-0 + top-0 in one layer — see slider_matrix.md (Q11)`,
    ];
  }
  if (/h-10|height:\s*['"]?40|height:\s*40/.test(content) && /scaleAnchorLabels/.test(content) && /ticks/.test(content)) {
    const headerBlock = content.match(/renderHeaderRow[\s\S]{0,2500}/)?.[0] ?? content;
    const rowContainers = (headerBlock.match(/relative|position:\s*['"]relative['"]/g) ?? []).length;
    if (rowContainers < 2 && !/header-stack|tick-row|anchor-row/i.test(headerBlock)) {
      return [
        `${sliderRel}: when both scaleAnchorLabels and ticks.marks — use two header rows (tick badges then anchors) — see slider_matrix.md`,
      ];
    }
  }
  return [];
}

/** FAIL when SLIDER_MATRIX renders tick badges inside row loop track. */
export function scanSliderPerRowTickBadges(projectRoot: string): string[] {
  if (!surveyPlanHasType(projectRoot, 'SLIDER_MATRIX')) return [];

  const sliderRel = findFirstExisting(projectRoot, [
    'src/components/SliderMatrixScale.tsx',
    'components/SliderMatrixScale.tsx',
  ]);
  if (!sliderRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, sliderRel), 'utf8');
  const rowLoop =
    content.match(/statementRows\.map[\s\S]{0,3500}/)?.[0] ??
    content.match(/statementRows\.map[\s\S]{0,3500}/)?.[0] ??
    '';
  if (!rowLoop) return [];

  if (/CustomSliderTrack[\s\S]{0,800}ticks\s*=/.test(rowLoop) || /tickValues\.map[\s\S]{0,400}CustomSliderTrack/.test(rowLoop)) {
    return [
      `${sliderRel}: SLIDER_MATRIX — tick badges belong in common header once, not per-row CustomSliderTrack — see slider_matrix.md (Q11)`,
    ];
  }
  if (/CustomSliderTrack[\s\S]{0,1200}tickLabels[\s\S]{0,400}<span|CustomSliderTrack[\s\S]{0,1200}tickLabels[\s\S]{0,400}<div/.test(rowLoop)) {
    return [
      `${sliderRel}: SLIDER_MATRIX — visible tick label DOM inside row track — use renderCommonTickHeader + trackOnly — see header-label-overlay.md`,
    ];
  }
  return [];
}

/** FAIL when CustomSliderTrack renders tooltip DOM while parent matrix has common header. */
export function scanMatrixTrackTooltipDom(projectRoot: string): string[] {
  const trackRel = findFirstExisting(projectRoot, [
    'src/components/CustomSliderTrack.tsx',
    'components/CustomSliderTrack.tsx',
  ]);
  if (!trackRel) return [];

  const track = fs.readFileSync(path.join(projectRoot, trackRel), 'utf8');
  const hasTooltipDom =
    /tooltip|Tooltip/.test(track) &&
    (/<span[\s\S]{0,200}tooltip|tooltip[\s\S]{0,400}<span|<div[\s\S]{0,200}tooltip|tooltip[\s\S]{0,400}<div/i.test(track) ||
      /isHovered|isDragging/.test(track));
  if (!hasTooltipDom) return [];

  const parentFiles = [
    'src/components/SliderMatrixScale.tsx',
    'components/SliderMatrixScale.tsx',
    'src/components/CsatMatrixScale.tsx',
    'components/CsatMatrixScale.tsx',
  ];
  for (const rel of parentFiles) {
    const abs = path.join(projectRoot, rel);
    if (!fs.existsSync(abs)) continue;
    const parent = fs.readFileSync(abs, 'utf8');
    if (/renderHeaderRow|GraphicsColumnLabelsRow|renderCommonTickHeader|renderCommonHeader/.test(parent)) {
      if (!/hideTooltip/.test(track) && !/hideTooltip/.test(parent)) {
        return [
          `${trackRel}: matrix parent ${rel} has common header — suppress in-track tooltip (hideTooltip) — see header-label-overlay.md`,
        ];
      }
    }
  }
  return [];
}

function surveyNeedsCustomSliderThumbChecks(projectRoot: string): {
  needsTrack: boolean;
  needsGraphics: boolean;
} {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const needsGraphics = variants.some(
    (v) =>
      (v.type === 'RATING_MATRIX' && /graphics/i.test(v.displayStyle)) ||
      (v.type === 'CSAT_MATRIX' && /graphics/i.test(v.displayStyle)),
  );
  const needsTrack =
    surveyPlanHasType(projectRoot, 'SLIDER_MATRIX') || needsGraphics;
  return { needsTrack, needsGraphics };
}

function customSliderGraphicsEmojiStripBelow(content: string): boolean {
  const graphicsBranch =
    content.match(/sliderType\s*===\s*['"]graphics['"][\s\S]{0,2500}/)?.[0] ?? '';
  if (graphicsBranch && /getEmojiForIndex/.test(graphicsBranch)) {
    return false;
  }
  if (
    /Array\.from\s*\(\s*\{\s*length:\s*ticks/.test(content) &&
    /getEmojiForIndex/.test(content)
  ) {
    return true;
  }
  const afterGraphics = content.split(/sliderType\s*===\s*['"]graphics['"]/)[1] ?? '';
  if (
    /mt-2|marginTop:\s*['"]8px|justify-between|justifyContent:\s*['"]space-between['"]/.test(
      afterGraphics.slice(0, 2000),
    ) &&
    /getEmojiForIndex/.test(content)
  ) {
    return true;
  }
  return false;
}

/** FAIL when slider/graphics survey lacks thumb lane centering, native thumb hide, or graphics emoji in thumb. */
export function scanCustomSliderThumbGeometryIssues(projectRoot: string): string[] {
  const { needsTrack, needsGraphics } = surveyNeedsCustomSliderThumbChecks(projectRoot);
  if (!needsTrack) return [];

  const trackRel = findFirstExisting(projectRoot, [
    'src/components/CustomSliderTrack.tsx',
    'components/CustomSliderTrack.tsx',
  ]);
  if (!trackRel) {
    return ['CustomSliderTrack.tsx missing — survey has SLIDER_MATRIX or graphics matrix — see custom-slider-track.md'];
  }

  const content = fs.readFileSync(path.join(projectRoot, trackRel), 'utf8');
  const issues: string[] = [];
  const top50Matches = content.match(/top:\s*['"]50%['"]/g) ?? [];
  if (top50Matches.length < 2) {
    issues.push(
      `${trackRel}: thumb + track must share vertical center (top: '50%' on lane) — see custom-slider-track.md § Slider lane`,
    );
  }
  if (
    !/webkit-slider-thumb|moz-range-thumb|cfm-hidden-range-thumb/.test(content)
  ) {
    issues.push(
      `${trackRel}: hide native range thumb (webkit/moz CSS or cfm-hidden-range-thumb) — see custom-slider-track.md`,
    );
  }
  if (needsGraphics && !/getEmojiForIndex/.test(content)) {
    issues.push(
      `${trackRel}: graphics matrix requires getEmojiForIndex in CustomSliderTrack thumb — see rating_matrix.md`,
    );
  }
  if (needsGraphics && customSliderGraphicsEmojiStripBelow(content)) {
    issues.push(
      `${trackRel}: graphics emoji must render on thumb — not a row below track — see rating_matrix.md`,
    );
  }
  if (
    /className\s*=\s*['"][^'"]*h-2/.test(content) &&
    /borderRadius:\s*['"]50%['"]/.test(content) &&
    top50Matches.length < 2
  ) {
    issues.push(
      `${trackRel}: do not anchor thumb inside h-2 bar only — use shared slider-lane — see custom-slider-track.md`,
    );
  }
  return issues;
}

/** FAIL when pointerLeave clears drag without pointer capture. */
export function scanCustomSliderPointerLeave(projectRoot: string): string[] {
  const trackRel = findFirstExisting(projectRoot, [
    'src/components/CustomSliderTrack.tsx',
    'components/CustomSliderTrack.tsx',
  ]);
  if (!trackRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, trackRel), 'utf8');
  const hasLeaveDeactivate =
    /onPointerLeave|onMouseLeave/.test(content) &&
    (/setIsDragging\s*\(\s*false\s*\)|setActive\s*\(\s*false\s*\)|setIsHovered\s*\(\s*false\s*\)[\s\S]{0,80}setIsDragging/.test(content));
  const hasCapture = /setPointerCapture|releasePointerCapture/.test(content);
  if (hasLeaveDeactivate && !hasCapture) {
    return [
      `${trackRel}: use pointer capture on range input — do not deactivate drag on pointerLeave alone — see custom-slider-track.md`,
    ];
  }
  return [];
}

/** FAIL when survey needs position helpers but scaleValues.ts lacks them. */
export function scanScaleValuesPositionHelpers(projectRoot: string): string[] {
  const variants = readMatrixVariantsFromPlan(projectRoot);
  const needsRatingGraphics = variants.some(
    (v) => v.type === 'RATING_MATRIX' && /graphics/i.test(v.displayStyle),
  );
  const needsDiscrete =
    needsRatingGraphics ||
    variants.some((v) => v.type === 'CSAT_MATRIX' && /graphics/i.test(v.displayStyle));
  const needsContinuous =
    surveyPlanHasType(projectRoot, 'SLIDER_MATRIX') ||
    variants.some((v) => v.type === 'CSAT_MATRIX' && /graphics/i.test(v.displayStyle)) ||
    needsRatingGraphics;

  if (!needsDiscrete && !needsContinuous) return [];

  const scaleRel = findFirstExisting(projectRoot, [
    'src/lib/surveyUi/scaleValues.ts',
    'lib/surveyUi/scaleValues.ts',
  ]);
  if (!scaleRel) {
    return ['lib/surveyUi/scaleValues.ts missing — add columnPercent and tickPercent — see scale-position-math.md'];
  }

  const content = fs.readFileSync(path.join(projectRoot, scaleRel), 'utf8');
  const issues: string[] = [];
  if (needsDiscrete && !/columnPercent|0\.5\s*\/\s*m/.test(content)) {
    issues.push(`${scaleRel}: RATING/CSAT graphics needs columnPercent — see scale-position-math.md`);
  }
  if (needsRatingGraphics && !/graphicsScaleInsetPercent/.test(content)) {
    issues.push(
      `${scaleRel}: RATING graphics needs graphicsScaleInsetPercent — see scale-position-math.md §RATING graphics`,
    );
  }
  if (needsRatingGraphics && !/graphicsLabelPercent/.test(content)) {
    issues.push(
      `${scaleRel}: RATING graphics needs graphicsLabelPercent — see scale-position-math.md §RATING graphics`,
    );
  }
  if (needsContinuous && !/tickPercent|anchorLabelPercentContinuous/.test(content)) {
    issues.push(`${scaleRel}: SLIDER_MATRIX/RATING graphics needs tickPercent (and anchorLabelPercentContinuous when anchors) — see scale-position-math.md`);
  }
  return issues;
}

/** FAIL when NPS hint labels use Question.tsx left-0/right-0 instead of columnPercent in RatingScale. */
export function scanNpsHintLabelAlignmentIssues(projectRoot: string): string[] {
  if (!surveyPlanHasType(projectRoot, 'NPS_SCALE')) return [];

  const ratingRel = findFirstExisting(projectRoot, [
    'src/components/RatingScale.tsx',
    'components/RatingScale.tsx',
  ]);
  if (!ratingRel) return [];

  const questionRel = findFirstExisting(projectRoot, QUESTION_CANDIDATES);
  const questionContent = questionRel
    ? fs.readFileSync(path.join(projectRoot, questionRel), 'utf8')
    : '';
  const ratingContent = fs.readFileSync(path.join(projectRoot, ratingRel), 'utf8');

  const hasHintLabels =
    /minLabel|midLabel|maxLabel/.test(questionContent) || /minLabel|midLabel|maxLabel/.test(ratingContent);
  if (!hasHintLabels) return [];

  const issues: string[] = [];
  if (!/columnPercent/.test(ratingContent)) {
    issues.push(
      `${ratingRel}: NPS hint labels must use columnPercent aligned to scale cells — see layout-cookbook/nps_scale.md`,
    );
  }
  if (
    questionRel &&
    /minLabel|midLabel|maxLabel/.test(questionContent) &&
    /left-0|right-0/.test(questionContent)
  ) {
    issues.push(
      `${questionRel}: NPS hint labels belong in RatingScale.tsx shared-width container — not left-0/right-0 in Question.tsx — see layout-cookbook/nps_scale.md`,
    );
  }
  return issues;
}

/** FAIL when RANK_ORDER dragAndDrop uses static Unranked list or lacks empty-rank fallback. */
export function scanRankOrderEmptyStateIssues(projectRoot: string): string[] {
  if (!surveyPlanHasType(projectRoot, 'RANK_ORDER')) return [];

  const rankRel = findFirstExisting(projectRoot, [
    'src/components/RankOrderScale.tsx',
    'components/RankOrderScale.tsx',
  ]);
  if (!rankRel) return [];

  const content = fs.readFileSync(path.join(projectRoot, rankRel), 'utf8');
  const isDragMode = /DndContext|dragAndDrop|interactionMode\s*===\s*['"]dragAndDrop['"]/.test(content);
  if (!isDragMode) return [];

  const issues: string[] = [];
  const hasStaticUnrankedUi =
    /Unranked\s+options/i.test(content) ||
    /['"`]Unranked['"`]/.test(content) ||
    />\s*Unranked/i.test(content);
  if (hasStaticUnrankedUi) {
    issues.push(
      `${rankRel}: do not use static "Unranked" list — all options sortable from first paint — see layout-cookbook/rank_order.md`,
    );
  }
  if (/getOrderedOptionIdsFromRanks/.test(content)) {
    const hasEmptyFallback =
      /displayIds/.test(content) ||
      /rankedIds\.length\s*>\s*0[\s\S]{0,120}\?\s*\[?\.\.\.rankedIds/.test(content) ||
      /rankedIds\.length\s*>\s*0[\s\S]{0,120}:\s*optionIds/.test(content) ||
      /ranked\.length\s*>\s*0[\s\S]{0,120}:\s*optionIds/.test(content);
    if (!hasEmptyFallback) {
      issues.push(
        `${rankRel}: dragAndDrop must fallback to all optionIds when ranks empty — see layout-cookbook/rank_order.md`,
      );
    }
  }
  return issues;
}

/** FAIL when §3c-r RANK_ORDER has image-capable optionDisplay but RankOrderScale lacks image branch. */
export function scanRankOrderOptionDisplayIssues(projectRoot: string): string[] {
  if (!surveyPlanHasType(projectRoot, 'RANK_ORDER')) return [];

  const variants = readRankOrderVariantsFromPlan(projectRoot);
  const needsImages = variants.some((v) => /imageOnly|textAndImage/i.test(v.optionDisplay));
  if (!needsImages) return [];

  const rankRel = findFirstExisting(projectRoot, [
    'src/components/RankOrderScale.tsx',
    'components/RankOrderScale.tsx',
  ]);
  if (!rankRel) {
    return [
      'RankOrderScale.tsx missing — §3c-r has image-capable optionDisplay — see rank_order.md §Option content',
    ];
  }

  const content = stripLineComments(
    fs.readFileSync(path.join(projectRoot, rankRel), 'utf8'),
  );
  const issues: string[] = [];

  const hasOptionDisplayBranch =
    /optionDisplay/.test(content) && /imageOnly|textAndImage|textOnly/.test(content);
  if (!hasOptionDisplayBranch) {
    issues.push(
      `${rankRel}: RANK_ORDER with image optionDisplay — branch on question.optionDisplay — see rank_order.md §Option content`,
    );
  }

  const hasImageRender =
    /<img\b|imageUrl|previewImageUrl|RankOrderOptionContent/.test(content);
  if (!hasImageRender) {
    issues.push(
      `${rankRel}: RANK_ORDER image optionDisplay requires <img> / imageUrl / previewImageUrl — see 14-rank-order.md §Implementation Recipe — option display`,
    );
  }

  if (
    /dangerouslySetInnerHTML[\s\S]{0,120}optionLabel/.test(content) &&
    !/imageOnly|textAndImage/.test(content)
  ) {
    issues.push(
      `${rankRel}: text-only rank rows without optionDisplay branch — use RankOrderOptionContent — see rank_order.md`,
    );
  }

  return issues;
}

/** §3c-m-gated: question shell must wire containerMedia, attachments, preview/download when fetch has media. */
export function scanQuestionMediaIssues(projectRoot: string): string[] {
  const variants = readMediaVariantsFromPlan(projectRoot);
  if (!variants.length) return [];

  const questionRel = findFirstExisting(projectRoot, QUESTION_CANDIDATES);
  if (!questionRel) {
    return ['Question.tsx missing — survey has question media in §3c-m — see layout-cookbook/question_media.md'];
  }

  const content = readQuestionMediaShellSource(projectRoot, questionRel);
  const issues: string[] = [];

  const hasContainerBranch = /containerMedia/.test(content);
  const hasAttachmentsBranch = /mediaAttachments/.test(content);
  const hasGridCols4 = /grid-cols-4|gridTemplateColumns.*repeat\(4/.test(content);
  const usesAltText = /altText|containerMedia\.altText/.test(content);
  const hasPreviewAffordance = /enablePreview|Preview/i.test(content);
  const hasDownloadAffordance = /enableDownload|download/i.test(content);

  const needsContainer = variants.some(v => v.hasContainerMedia === 'yes');
  const needsAttachments = variants.some(v => parseInt(v.attachmentCount, 10) > 0);
  const needsPreview = variants.some(v => v.enablePreview === 'true');
  const needsDownload = variants.some(v => v.enableDownload === 'true');
  const alignments = new Set(
    variants.map(v => v.alignment).filter(a => a && a !== '—'),
  );

  if (needsContainer && !hasContainerBranch) {
    issues.push(
      `${questionRel}: §3c-m has container media — render question.containerMedia when url present — see 00-question-shell.md`,
    );
  }

  for (const alignment of alignments) {
    if (!content.includes(alignment)) {
      issues.push(
        `${questionRel}: §3c-m alignment ${alignment} — branch layout in QuestionMediaBlock or Question.tsx — see layout-cookbook/question_media.md`,
      );
    }
  }

  if (needsAttachments && (!hasAttachmentsBranch || !hasGridCols4)) {
    issues.push(
      `${questionRel}: §3c-m has media attachments — render mediaAttachments in 4-column grid — see layout-cookbook/question_media.md`,
    );
  }

  if (needsPreview && !hasPreviewAffordance) {
    issues.push(
      `${questionRel}: §3c-m enablePreview — hover Preview on attachment tiles when question.enablePreview — see layout-cookbook/question_media.md`,
    );
  }

  if (needsDownload && !hasDownloadAffordance) {
    issues.push(
      `${questionRel}: §3c-m enableDownload — hover Download on attachment tiles when question.enableDownload — see layout-cookbook/question_media.md`,
    );
  }

  if (needsContainer && !usesAltText) {
    issues.push(
      `${questionRel}: §3c-m container media — use containerMedia.altText on primary img — not hardcoded alt=""`,
    );
  }

  return issues;
}

export function scanLayoutFormatIssues(projectRoot: string): string[] {
  return [
    ...scanBipolarAnchorHeaderIssues(projectRoot),
    ...scanBipolarHeaderRegimeIssues(projectRoot),
    ...scanCarouselScaleHeaderIssues(projectRoot),
  ];
}

export function scanInteractionIssues(projectRoot: string): string[] {
  return [
    ...scanLikertDisplayStyleMisuse(projectRoot),
    ...scanCsatStarIconAntiPattern(projectRoot),
    ...scanCsatMatrixStarGridIssues(projectRoot),
    ...scanMcqOtherPickerIssues(projectRoot),
    ...scanHeatmapMaxClicksIssues(projectRoot),
    ...scanRatingGraphicsIndexMapIssues(projectRoot),
    ...scanMatrixDuplicateHeaderIssues(projectRoot),
    ...scanSliderDualHeaderIssues(projectRoot),
    ...scanSliderPerRowTickBadges(projectRoot),
    ...scanMatrixTrackTooltipDom(projectRoot),
    ...scanCustomSliderPointerLeave(projectRoot),
    ...scanCustomSliderThumbGeometryIssues(projectRoot),
    ...scanScaleValuesPositionHelpers(projectRoot),
    ...scanNpsHintLabelAlignmentIssues(projectRoot),
    ...scanRankOrderEmptyStateIssues(projectRoot),
    ...scanRankOrderOptionDisplayIssues(projectRoot),
    ...scanMatrixCellBorderStyleIssues(projectRoot),
    ...scanSliderScaleZoneIssues(projectRoot),
    ...scanBipolarSliderIssues(projectRoot),
    ...scanCarouselScaleHeaderIssues(projectRoot),
    ...scanCsatMatrixCellCenteringIssues(projectRoot),
    ...scanCsatMatrixNaPlacementIssues(projectRoot),
    ...scanCsatMatrixDenseAnchorGridIssues(projectRoot),
    ...scanLikertMatrixCellCenteringIssues(projectRoot),
    ...scanQuestionMediaIssues(projectRoot),
  ];
}

export function scanMcqOtherIssues(projectRoot: string): string[] {
  if (readMcqOtherFlagFromPlan(projectRoot) !== true) return [];

  const questionRel = findFirstExisting(projectRoot, QUESTION_CANDIDATES);
  if (!questionRel) {
    return [
      'Question.tsx missing — survey has MCQ Other options; author MCQ handler with MCQ_OTHER_TEXT_CHANGE',
    ];
  }

  const content = fs.readFileSync(path.join(projectRoot, questionRel), 'utf8');
  if (/MCQ_OTHER_TEXT_CHANGE|buildMcqOptionLevelKey/.test(content)) return [];

  return [
    `${questionRel}: survey has MCQ Other options — wire MCQ_OTHER_TEXT_CHANGE + buildMcqOptionLevelKey — see 02-radio.md`,
  ];
}

export interface SourceScanResult {
  stubMatches: string[];
  internalSdkImports: string[];
  nextImports: string[];
  introStartIssues: string[];
  hardcodedStyleIssues: string[];
  endPageCtaIssues: string[];
  matrixStatementIssues: string[];
  matrixLayoutIssues: string[];
  mcqOtherIssues: string[];
}

export function scanProjectSource(projectRoot: string): SourceScanResult {
  const stubMatches: string[] = [];
  const internalSdkImports: string[] = [];
  const nextImports: string[] = [];

  for (const file of collectSourceFiles(projectRoot)) {
    const content = fs.readFileSync(file, 'utf8');
    const rel = path.relative(projectRoot, file);

    if (content.includes('not yet implemented')) {
      stubMatches.push(`${rel}: stub placeholder text`);
    }
    if (/@explorer02\/cfm-survey-sdk\/src|@repo\/sdk\/src/.test(content)) {
      internalSdkImports.push(`${rel}: internal SDK import`);
    }
    if (/from\s+['"]next\//.test(content)) {
      nextImports.push(`${rel}: stale next/* import`);
    }
  }

  const introStartIssues = scanIntroStartIssues(projectRoot);
  const hardcodedStyleIssues = scanHardcodedStyleIssues(projectRoot);
  const endPageCtaIssues = scanEndPageCtaIssues(projectRoot);
  const matrixStatementIssues = scanMatrixStatementIssues(projectRoot);
  const matrixLayoutIssues = scanMatrixLayoutIssues(projectRoot);
  const mcqOtherIssues = scanMcqOtherIssues(projectRoot);

  return {
    stubMatches,
    internalSdkImports,
    nextImports,
    introStartIssues,
    hardcodedStyleIssues,
    endPageCtaIssues,
    matrixStatementIssues,
    matrixLayoutIssues,
    mcqOtherIssues,
  };
}
